/**
 * Typed wrappers for CortexV2's non-CRUD backend calls.
 *
 * MIGRATION STATE (2026-07): the legacy `endpoints` monolith is being retired.
 * Calls now target the split services via the Firebase Hosting rewrites:
 *   - /api/v2/bqv/**     → socialiq-bqv     (read-only BigQuery named queries)
 *   - /api/v2/sql/**     → socialiq-sql     (MySQL CRUD + named queries)
 *   - /api/v2/content/** → socialiq-content (advertisers_content + seasonal_dates CRUD + GCS upload +
 *                          media transforms replace_audio/optimize_image — in-service ffmpeg + Pillow +
 *                          clean-urls, forwarded to socialiq-marketplace with content's own key)
 *   - /api/v2/ai/**      → socialiq-ai      (AI image generation + Ask Data only)
 *   - /api/v2/launcher/**→ socialiq-launcher   (template→spec expansion + async ad-launch pipeline)
 *   - /api/v2/automation/** → socialiq-automation (rules engine: dry-run + HITL approve/deny)
 *   - /api/v2/supabase/** → socialiq-supabase (read-only proxy onto joylink's own Supabase project —
 *                          today just boosting_waitlist; see its README for the allowlist)
 * All non-CRUD write-actions are now LIVE on split services — no NotMigrated stubs remain. The monolith
 * `endpoints` router is no longer needed by CortexV2.
 */
import { api, ApiError, getBlob } from "./client";
import type { V2AmazonAdset, ImpactAdset, PerfRow, BrandAccount, AudioTag, ContentStatus, MerchantUrl } from "../types";
import type { InternalReportRequest, InternalReportResponse } from "../views/internalReport/contract";
import type { CopyAnswer } from "../views/internalReport/copy";

const SQL = "/api/v2/sql";
const BQV = "/api/v2/bqv";
const CONTENT = "/api/v2/content";
const AI = "/api/v2/ai";
const LAUNCHER = "/api/v2/launcher";   // socialiq-launcher: template→spec expansion + ad-launch pipeline
const AUTOMATION = "/api/v2/automation"; // socialiq-automation: rules engine (dry-run + HITL approve/deny)
const INVOICING = "/api/v2/invoicing"; // socialiq-invoicing: recomputes a creator's month due + issues a Stripe invoice
const SUPABASE = "/api/v2/supabase"; // socialiq-supabase: read-only proxy onto joylink's own Supabase project

// ── Autolauncher actions ──────────────────────────────────────────────────────

// One creator the launcher refused to launch for — e.g. no facebook_pages row mapped to their a_id
// (expander.py's "missing facebook page config" / "missing creator/page config"), no available
// Amazon tags, not enrolled in the Impact program, etc. `reason` is a short, human-readable string.
// `advertiser_name` is only populated for skip reasons reached after expander.py has already looked
// the creator up (the facebook-page checks); it's null/absent for earlier checks (Amazon tags, Impact
// program) and when the creator lookup itself is what failed.
export interface SkippedCreator { a_id: number; advertiser_name?: string | null; reason: string }

// Live: socialiq-launcher runs the template across its active creators (cron path), recording an
// execution per creator. Returns {success, count, message, skipped}. Unattended, so there is no UI
// to show `skipped` in — service.py posts a beast-notifications Slack alert per skip instead.
export const runTemplate = (templateId: number) =>
  api.post<{ success: boolean; count: number; message: string; skipped: SkippedCreator[] }>(
    `${LAUNCHER}/templates/${templateId}/run`, {});

// Read: the next scheduled fire of the autolauncher sweep, for the Dashboard's "Next run" line.
// COMPUTED on read from the launcher's config'd cron cadence (never stale) — replaces the old Retool
// `crons`-table `next_run` (a stored value that went stale) and the CortexV2 hardcoded placeholder.
// next_run is an ISO-8601 UTC string, or null if the cadence can't be parsed (render a fallback).
export const getNextCronRun = () =>
  api.get<{ next_run: string | null; cron: string; timezone: string; source: string }>(
    `${LAUNCHER}/next_cron_run`);

// Ad Copy Review modal (Launcher's "Confirm launch" flow) — the shape the backend returns/expects
// for one piece of content's editable ad copy. categories is a passthrough (not edited in the UI).
export interface AdCopyEdit {
  headline:     string;
  primary_text: string;
  description:  string;
  categories?:  string[];
  // SOC-637, read-only passthroughs on generated copy: the text Gemini transcribed from the creative
  // before writing (so a buyer can see whether it read the deal), and which model wrote it.
  visible_text?: string;
  model?:        string;
}

// One row of the preview/reroll result — the modal renders cards from these. Failed items show
// error + Reroll only; successful items show editable fields + Reroll.
export interface AdCopyReviewItem {
  ok:            boolean;
  ac_id:         number;
  media_url?:    string;
  media_type?:   string;
  merchant_url?: string;
  ad_copy?:      AdCopyEdit;
  error?:        string;
}

export interface LaunchPayload {
  template_id: number;
  content_ids: number[];
  exclusions?: { content_id: number; reason: string }[];
  adset_mode?: "batch" | "per_ad" | "existing";
  adset_id?: string;
  buyer_email?: string;
  // When the ads should start delivering, as a UTC instant (`2026-07-28T13:00:00Z`).
  // Omit to start immediately — the default. An instant needs no interpretation, so it means the same
  // moment from any timezone and the two ill-defined Pacific wall clocks (the hour skipped each spring,
  // the hour repeated each autumn) cannot arise. The launcher resolves it against its own clock: a moment
  // that has already passed becomes "start now", so a value that went stale mid-flow needs no re-picking.
  start_date?: string;
  // Operator-reviewed ad copy from the Ad Copy Review modal, keyed by ac_id (as a string). When
  // present, the backend skips the Gemini call for matched contents and uses this copy verbatim.
  ad_copies?: Record<string, AdCopyEdit>;
  // SOC-632: the Launch Review modal's per-content landing-URL pick, keyed by content_id (as a
  // string). When present and the url is one of that content's own merchant urls, it's used
  // verbatim; otherwise the backend matches the recipe's merchant against the content's merchant
  // urls, falling back to the existing single-url behavior — see autolauncher/merchant_url.py.
  merchant_urls?: Record<string, string>;
}

// Live: socialiq-launcher expands the template + selected content into launch specs and enqueues them
// to the ad-creation pipeline. Returns {success, count, message, skipped}. (The service runs mock-Meta
// on staging — FORCE_PAUSED guard on — until real Meta is deliberately enabled.)
// `start_time` in the response is what was ACTUALLY scheduled (null = immediate), which can differ
// from what was sent: the server resolves against its own clock, so report this rather than the request.
export const launchTemplate = (payload: LaunchPayload) =>
  api.post<{ success: boolean; count: number; message: string; skipped: SkippedCreator[];
             start_time: string | null }>(
    `${LAUNCHER}/templates/launch`, payload);

// Live: generate one ad copy per content_id in parallel (Gemini, grounded in the content's merchant
// URL when available). Each item includes either ad_copy or error. Powers the Ad Copy Review modal's
// "Generate with AI" button.
export const previewAdCopies = (templateId: number, contentIds: number[]) =>
  api.post<{ items: AdCopyReviewItem[] }>(`${LAUNCHER}/preview_ad_copies`,
    { template_id: templateId, content_ids: contentIds });

// Live: regenerate ad copy for a SINGLE content — the modal's per-card reroll button.
export const rerollAdCopy = (templateId: number, contentId: number) =>
  api.post<{ item: AdCopyReviewItem }>(`${LAUNCHER}/reroll_ad_copy`,
    { template_id: templateId, content_id: contentId });

// ── Async ad-copy generation queue (Review tab) ───────────────────────────────
// Replaces the two calls above with a fire-and-forget queue: enqueue returns instantly with the
// queued item count (drives the "N ads in queue" toast), a Cloud Tasks worker generates each item in
// the background, and the buyer reviews/edits/launches from the Ad Copy Review view once ready.

export interface AdCopyJobItem {
  id:            number;
  job_id:        number;
  content_id:    number;
  ac_id:         number | null;
  status:        "queued" | "processing" | "ready" | "error";
  ad_copy:       AdCopyEdit | null;
  media_url:     string | null;
  media_type:    string | null;
  merchant_url:  string | null;
  error:         string | null;
  attempts:      number;
  /** advertisers.advertiserName for this content's creator — null if the content row (or its
   *  advertiser) no longer exists by the time the buyer reviews. */
  creator_name:  string | null;
  /** This content's creator (advertisers_content.a_id) — how a launch's `skipped` creators are
   *  matched back to their cards. null if the content row no longer exists. */
  a_id:          number | null;
  /** Why the launcher would skip this creator's ads right now (e.g. "creator not enrolled in impact
   *  program") — the same check it runs at launch, read live on every poll. A held card is never
   *  sent on Launch and stays on the Review tab; it clears by itself once the cause is fixed. */
  hold_reason:   string | null;
}

// Everything a LaunchPayload needs besides template_id/content_ids/ad_copies — the Launch Review
// modal's choices, captured at enqueue time so the Review view's Launch button needs no re-pick.
export interface AdCopyLaunchSpec {
  exclusions?: { content_id: number; reason: string }[];
  adset_mode?: "batch" | "per_ad" | "existing";
  adset_id?:   string;
  start_date?: string;
  // SOC-632: the Launch Review modal's landing-URL picks, keyed by content_id (as a string) — see
  // LaunchPayload.merchant_urls.
  merchant_urls?: Record<string, string>;
  // SOC-637: the Gemini model to write this batch's copy with — one of listAdCopyModels().models.
  // Omitted = the launcher's default.
  model?: string;
}

// SOC-637: the models a buyer may pick for ad copy, and the default — read from the launcher so the
// picker can never offer a model the backend would refuse.
export const listAdCopyModels = () =>
  api.get<{ models: string[]; default: string }>(`${LAUNCHER}/ad_copy_models`);

export interface AdCopyJob {
  job_id:       number;
  template_id:  number;
  status:       "queued" | "processing" | "ready" | "partial" | "error";
  item_count:   number;
  launch_spec:  AdCopyLaunchSpec | null;
  consumed_at:  string | null;
  created:      string;
  updated:      string;
  items:        AdCopyJobItem[];
}

// Queue generation for N content_ids (bulk "generate copy") or one (reroll — pass a single-element
// array). Returns immediately; generation happens in the background — see listAdCopyJobs. launchSpec
// rides along for the eventual Review-view launch (see AdCopyLaunchSpec).
export const enqueueAdCopyJob = (templateId: number, contentIds: number[], launchSpec?: AdCopyLaunchSpec) =>
  api.post<{ job_id: number; item_count: number }>(`${LAUNCHER}/ad_copy_jobs`,
    { template_id: templateId, content_ids: contentIds, ...(launchSpec ?? {}) });

// This buyer's not-yet-consumed jobs, each with its items — polled in the background (toasts on a
// ready/partial/error transition) and rendered by the Ad Copy Review view's inbox.
export const listAdCopyJobs = () =>
  api.get<{ jobs: AdCopyJob[] }>(`${LAUNCHER}/ad_copy_jobs`);

// Marks a job consumed once the buyer launches from the Review view, so it stops appearing in
// listAdCopyJobs (and stops re-triggering a "ready" toast).
export const consumeAdCopyJob = (jobId: number) =>
  api.post<{ ok: boolean; job_id: number }>(`${LAUNCHER}/ad_copy_jobs/${jobId}/consume`, {});

// Marks the items that actually went out in a Review-view launch. They drop out of listAdCopyJobs;
// held or skipped items stay with their reason. The job is consumed once nothing unlaunched is left
// (job_consumed: true).
export const markAdCopyJobItemsLaunched = (jobId: number, itemIds: number[]) =>
  api.post<{ ok: boolean; job_id: number; launched: number; job_consumed: boolean }>(
    `${LAUNCHER}/ad_copy_jobs/${jobId}/launched`, { item_ids: itemIds });

// Re-queues ONE errored item in place (same job/batch) — the Review view's per-card "Retry" button.
export const retryAdCopyJobItem = (jobId: number, itemId: number) =>
  api.post<{ ok: boolean; item_id: number }>(`${LAUNCHER}/ad_copy_jobs/${jobId}/items/${itemId}/retry`, {});

// Removes ONE item outright (any status) — the Review view's per-card cancel/remove button, for a
// creative added to the batch by mistake. If it was the last item, the whole job is deleted too
// (job_deleted: true).
export const cancelAdCopyJobItem = (jobId: number, itemId: number) =>
  api.delete<{ ok: boolean; job_id: number; item_id: number; job_deleted: boolean }>(
    `${LAUNCHER}/ad_copy_jobs/${jobId}/items/${itemId}`);

// Read: adset type-ahead — live via socialiq-sql named query getAdsetSearch (active v2 Amazon
// adsets by name substring, optionally one advertiser). Returns a bare array; wrap as { results }
// for the caller. Empty query / no a_id -> those filters are skipped server-side.
export const searchAdsets = (query: string, aId?: string | null) =>
  api.post<V2AmazonAdset[]>(`${SQL}/queries/getAdsetSearch`, {
    q: query || null,
    a_id: aId != null && aId !== "" ? Number(aId) : null,
  }).then(rows => ({ results: rows }));

// Read: the Impact counterpart (SOC-503) — adsets that already hold Impact ads, via getImpactAdsetSearch.
export const searchImpactAdsets = (query: string, aId?: string | null) =>
  api.post<ImpactAdset[]>(`${SQL}/queries/getImpactAdsetSearch`, {
    q: query || null,
    a_id: aId != null && aId !== "" ? Number(aId) : null,
  }).then(rows => ({ results: rows }));

// ── Launcher Tracker ──────────────────────────────────────────────────────────

// Read: pipeline status — live via socialiq-sql's composite launcher-tracker read. Returns
// {items:[...]} where each item nests its campaigns/adsets/ads/failures (batch children folded in).
export const listPipelineStatus = (limit = 50, since?: string) =>
  api.post<{ items: Record<string, unknown>[] }>(`${SQL}/pipeline_status`, {
    limit, ...(since ? { since } : {}),
  });

// Live: re-drive a failed pipeline step via socialiq-launcher (clears idempotency markers, re-publishes
// the stored stage payload). Correct for launcher-created failures; legacy monolith failures in the
// shared pipeline_failures table are a coexistence edge until the monolith is retired.
export const retryPipelineStep = (pfId: number, payload?: Record<string, unknown>) =>
  api.post<{ ok: boolean; pf_id: number; step: string }>(
    `${LAUNCHER}/retry`, { pf_id: pfId, ...(payload || {}) });

// ── Meta-write actions (PerformanceView) — LIVE via socialiq-launcher ────────────────────────────
// The launcher runs mock-Meta + FORCE_PAUSED on staging, so these never touch a live ad account there.

// PerformanceView onBulkChange: apply bulk pause/activate/budget changes to Meta entities. The payload
// (account_id, ts_id, level, changes[]) is exactly the launcher /changes body. Applied synchronously
// via the MetaClient write seam. Returns {status, applied}.
export const bulkChange = (payload: {
  account_id: string; ts_id: "FB"; level: "ad" | "adset" | "campaign";
  changes: { remote_id: string; change_type: "status" | "budget"; old_value: string | number; new_value: string | number }[];
}) =>
  api.post<{ status: string; applied: number }>(`${LAUNCHER}/changes`, payload);

// PerformanceView onClone: replicate an existing adset (or single ad) as a fresh launch. The full spec
// (objective/targeting/account) is rebuilt server-side from the source ad's original launch spec; this
// form only overrides names/budget/start_date. Flows through the same campaign→adset→ad pipeline.
export const cloneAds = (form: {
  // The Brand Performance breakdown copies at four levels, and each means something different:
  //   advertiser → a new campaign per source campaign, with their ad sets and ads
  //   campaign   → one new campaign, a new ad set per source ad set, their ads
  //   adset      → a new ad set under the source campaign, its ads
  //   ad/legacy  → one ad, into `into_adset_id` if the operator picked one, else a new ad set
  // `traffic_days` limits the first three to ads that actually delivered recently — an ad set
  // accumulates ads, and copying the dead ones buys nothing.
  clone_target: "advertiser" | "campaign" | "adset" | "ad" | "legacy";
  adset_id?: string; ad_id?: string; campaign_id?: string; campaign_ids?: string[];
  account_id?: string; into_adset_id?: string; traffic_days?: number;
  campaign_name?: string;
  adset_name?: string; ad_name?: string; creative_name?: string; budget?: number; start_date?: string;
  // BRANDS ONLY (see cloneBrandAds): report the copy to a specific pixel instead of the source's.
  pixel_id?: string;
}) =>
  api.post<CloneResponse>(`${LAUNCHER}/clone`, form);

type CloneResponse = {
  status: string; r_id?: number; clone_target: string; ads: number; error?: string;
  adsets?: number; campaigns?: number; skipped_adsets?: string[];
  requests?: { status: string; r_id?: number; campaign_name?: string }[];
};

/**
 * The BRAND copy. Same body, same levels, same response as `cloneAds` — one rule differs.
 *
 * `/clone` re-derives the pixel from launcher config on every copy, which is right for a creator
 * launch (the platform's pixel is what is being optimised for) and wrong for a brand: the config
 * knows only ONE global default, so a brand's copy reported to somebody else's pixel. `/clone-brand`
 * reads the pixel Meta actually has — the ad's own tracking_specs, else its ad set's promoted_object,
 * else the campaign's — and writes it back to the same level on the copy. `pixel_id` overrides it.
 */
export const cloneBrandAds = (form: Parameters<typeof cloneAds>[0]) =>
  api.post<CloneResponse>(`${LAUNCHER}/clone-brand`, form);

// URL Creator tool: mint a one-off tracking link (Impact program promo, or an Amazon adset boost)
// outside the normal launch pipeline. Amazon v1 requires adset_id to already exist in our DB (a 404
// with a clear message otherwise — see socialiq_launcher/core/manual_tracking.py).
// b_id is deliberately NOT a param here — the backend resolves the buyer from the authenticated
// caller's own session email (buyers.email), so the operator never has to pick their own identity.
// `brands` (SOC-324) is a DIRECT advertiser's own destination: no affiliate call, no deeplink, no
// tag — it is wrapped in a redirector domain and given a per-ad tracking_id, and the advertiser's
// `advertisers.url_scheme` row decides what else is completed. `tracking_platform` is only the
// FALLBACK for an advertiser who has no scheme of their own; omit it and nothing is invented.
export const buildTrackingLink = (payload: {
  network: "impact" | "amazon" | "brands"; a_id: string | number;
  program_id?: string | number; product_name?: string; product_url: string;
  is_social_deeplink?: boolean; deeplink_provider?: "joylink" | "urlgenius"; adset_id?: string;
  tracking_platform?: string;
}) =>
  api.post<{ tracking_link?: string; tracking_id?: string; error?: string }>(
    `${LAUNCHER}/build_tracking_link`, payload);

// ── Automation actions — LIVE via socialiq-automation (the rules engine) ────────────────────────
// decided_by is server-derived from the authenticated identity (not sent from the client), so the
// audit trail can't be forged. The service runs mock-Meta + mock-BigQuery on staging.

export const dryRunRule = (arId: number) =>
  api.post<{ ar_id: number; name: string; would_affect: number; simulation: unknown[]; error?: string }>(
    `${AUTOMATION}/dry_run_rule`, { ar_id: arId });

export const approveExecution = (areId: number, _decidedBy?: string) =>
  api.post<Record<string, unknown>>(`${AUTOMATION}/approve_execution`, { are_id: areId });

export const denyExecution = (areId: number, _decidedBy?: string) =>
  api.post<Record<string, unknown>>(`${AUTOMATION}/deny_execution`, { are_id: areId });

export const approveExecutionItem = (areId: number, entityId: string, _decidedBy?: string) =>
  api.post<Record<string, unknown>>(`${AUTOMATION}/approve_execution_item`, { are_id: areId, entity_id: entityId });

export const denyExecutionItem = (areId: number, entityId: string, _decidedBy?: string) =>
  api.post<Record<string, unknown>>(`${AUTOMATION}/deny_execution_item`, { are_id: areId, entity_id: entityId });

// Native: socialiq-ai turns a natural-language schedule into a cron expression (Gemini). Returns
// 503 if Vertex isn't configured yet. The rest of the automation rule engine is still stubbed.
export const generateCron = (description: string) =>
  api.post<{ cron: string }>(`${AI}/generate_cron`, { description });

// ── Content actions ───────────────────────────────────────────────────────────

export interface UploadFileParams {
  /** Raw base64, the single-asset path's input. Optional only so `file` can be used instead —
   *  exactly one of the two must be present. */
  base64?: string;
  /** THE QUEUE'S INPUT. A File goes to GCS as bytes; base64 inflates it ~4/3 in memory before it
   *  is decoded back, which for a 20-clip batch is the difference between a browser that copes and
   *  one that does not. The single-asset path keeps base64 — it reads the file itself and has one
   *  of them in hand either way. */
  file?: File;
  /** Progress 0-100 for a row's bar. Present ⇒ the PUT runs on XHR, which reports upload progress;
   *  absent ⇒ plain fetch, exactly as before. */
  onProgress?: (pct: number) => void;
  /** The pre-inserted row, as soon as signed-url returns it. A retry hands it back via `existing`
   *  so the second attempt reuses the row instead of orphaning the first. */
  onRowCreated?: (row: { ac_id: number; signed_url: string; public_url: string }) => void;
  /** Set on a RETRY: reuse the row + signed URL from the first attempt. signed-url PRE-INSERTS a
   *  row, so calling it twice for one asset leaves an orphan behind. */
  existing?: { ac_id: number; signed_url: string; public_url: string };
  filename: string;
  content_type: string;
  a_id: string;
  source: string;
  status: string;
  audio_tag: string | null;
  merchant_urls: unknown[];
  niche: string;
  seasonal: boolean;
  start_date: string;
  end_date: string;
  requires_starting_hour: boolean;
  starting_hour: number;
  size_kb: number;
}

// Signed-URL upload (POST /api/v2/content/signed-url + a direct-to-GCS PUT), NOT the base64
// /upload endpoint — base64-over-JSON inflates a file by ~4/3 and both Cloud Run's ~32MB HTTP/1
// body cap AND (worse) Firebase Hosting's rewrite proxy sit in front of it; Cloud Run alone gives
// a clean 413, but through the Hosting rewrite an oversized body surfaces as an opaque 500. Video
// routinely crosses that line (a 24MB+ clip is common); images rarely do, but this path is used
// for everything so there's only one upload code path to maintain. The GCS bucket needs a CORS
// rule allowing PUT with Content-Type from Cortex's origin(s) for this to work from a browser.
async function getSignedUploadUrl(params: {
  a_id: number; filename: string; content_type: string; status: string; source: string;
  audio_tag?: string | null; merchant_urls?: unknown[]; niche?: string; seasonal?: boolean;
  start_date?: string; end_date?: string; requires_starting_hour?: boolean; starting_hour?: number;
}) {
  return api.post<{ signed_url: string; public_url: string; blob_path: string; ac_id: number }>(
    `${CONTENT}/signed-url`, params,
  );
}

export const uploadContentFile = async (
  params: UploadFileParams,
): Promise<{ success: boolean; ac_id: number; url: string; signed_url: string; public_url: string }> => {
  const { ac_id, public_url, signed_url } = params.existing ?? await getSignedUploadUrl({
    a_id: Number(params.a_id),
    filename: params.filename,
    content_type: params.content_type,
    status: params.status,
    source: params.source,
    audio_tag: params.audio_tag,
    merchant_urls: params.merchant_urls,
    niche: params.niche,
    seasonal: params.seasonal,
    start_date: params.start_date,
    end_date: params.end_date,
    requires_starting_hour: params.requires_starting_hour,
    starting_hour: params.starting_hour,
  });
  if (!params.existing) params.onRowCreated?.({ ac_id, signed_url, public_url });

  // params.base64 is already the raw base64 (UploadModal strips the "data:<mime>;base64," prefix
  // before calling us) — tolerate a prefix anyway in case a future caller doesn't.
  // A File goes as-is; base64 is decoded to bytes first.
  let body: Blob | Uint8Array;
  if (params.file) {
    body = params.file;
  } else {
    const b64 = params.base64 ?? "";
    const raw = b64.includes(",") ? b64.slice(b64.indexOf(",") + 1) : b64;
    const binary = atob(raw);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    body = bytes;
  }

  // Plain fetch, NOT the `api` client — the signed URL carries its own auth in the query string,
  // and sending our Firebase Bearer token / forcing JSON content-type would fight the v4
  // signature GCS expects (it was signed for exactly this Content-Type and no other headers).
  // XHR only when a caller wants progress: fetch cannot report UPLOAD progress, and swapping the
  // single-asset path onto XHR to share one code path would change a path nobody asked to change.
  if (params.onProgress) {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signed_url, true);
      xhr.setRequestHeader("Content-Type", params.content_type);
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) params.onProgress!(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300)
        ? resolve()
        : reject(new ApiError(xhr.status, xhr.responseText, `Upload to storage failed (${xhr.status})`));
      xhr.onerror = () => reject(new ApiError(0, "", "Upload to storage failed (network)"));
      xhr.send(body as XMLHttpRequestBodyInit);
    });
  } else {
    const putRes = await fetch(signed_url, {
      method: "PUT",
      headers: { "Content-Type": params.content_type },
      body: body as BodyInit,
    });
    if (!putRes.ok) {
      const detail = await putRes.text().catch(() => "");
      throw new ApiError(putRes.status, detail, `Upload to storage failed (${putRes.status})`);
    }
  }

  // The pre-inserted row has size_kb=0 until this backfill lands — a top-level column, so this
  // PATCH can't clobber the metadata JSON the way patching a metadata-backed field alone would.
  await api.put(`${CONTENT}/advertisers_content/${ac_id}`, { size_kb: params.size_kb });

  // SOC-284: auto-scan videos here. Fail-open — upload already succeeded; operator can still Rescan if detection fails.
  if (params.content_type.toLowerCase().startsWith("video/")) {
    try {
      await runMusicDetection(ac_id);
    } catch {
      /* ignore */
    }
    // Video-optimize: same fail-open shape. The pre-inserted row's video_optimize_ready=0
    // (socialiq_content/core/upload.py) is what actually gates joylink-facing visibility for a
    // video; this content grid doesn't filter on it, so the row is visible here immediately
    // regardless (unlike upload_completed, unaffected either way).
    try {
      await triggerVideoOptimize(ac_id);
    } catch {
      /* ignore */
    }
  }

  return { success: true, ac_id, url: public_url, signed_url, public_url };
};

// Avatar image upload — a signed URL with no advertisers_content row behind it (unlike
// uploadContentFile above): an avatar isn't content-library content, so socialiq_content hands back
// signed_url/public_url only. The caller PUTs the bytes then stores public_url itself (Creator
// modal writes it into advertisers.avatar_image_url via the normal creators CRUD call).
async function getAvatarSignedUrl(contentType: string) {
  return api.post<{ signed_url: string; public_url: string }>(
    `${CONTENT}/avatar-signed-url`, { content_type: contentType },
  );
}

// Direct-to-GCS PUT, same reasoning as uploadContentFile's PUT: the signed URL carries its own
// auth, so this bypasses the `api` client rather than fighting its forced JSON header / Firebase
// bearer token against the v4 signature. Simpler than uploadContentFile's base64 dance — a File is
// already a valid fetch() body, no FileReader round-trip needed.
export async function uploadAvatarImage(file: File): Promise<string> {
  const { signed_url, public_url } = await getAvatarSignedUrl(file.type);
  const res = await fetch(signed_url, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new ApiError(res.status, detail, `Upload to storage failed (${res.status})`);
  }
  return public_url;
}

// Native: partial CRUD update of the advertiser's `active` flag (advertisers_PartialUpdate).
export const toggleCreatorActive = (aId: string, active: boolean) =>
  api.put<Record<string, unknown>>(`${SQL}/advertisers/${aId}`, { active: active ? 1 : 0 });

export interface AIGenerateParams {
  a_id: string;
  prompt: string;
  aspect_ratio?: string;
  reference_image_urls?: string[];
  hero_idx?: number;
  niche?: string;
}

// Native: socialiq-ai expands the brief (Gemini) then generates the image (Helm) and returns base64.
// a_id is coerced to the int the API expects. Returns 503 if AI creds aren't provisioned yet.
export const generateAIImage = (params: AIGenerateParams) =>
  api.post<{ success: boolean; image_base64: string; content_type: string; prompt_used: string; subject_id?: string }>(
    `${AI}/generate_image`, { ...params, a_id: Number(params.a_id) });

// socialiq-content media transforms — moved here from socialiq-ai (media ops, not AI). Implemented
// in-service via ffmpeg + Pillow (socialiq_content/core/media.py); returns {status, url, output_gcs_path}.
export const replaceAudio = (videoGcsPath: string, audioName: string) =>
  api.post<{ status: string; url: string; output_gcs_path: string }>(
    `${CONTENT}/replace_audio`, { video_gcs_path: videoGcsPath, audio_name: audioName });

export const optimizeImage = (imageGcsPath: string) =>
  api.post<{ status: string; url: string; output_gcs_path: string }>(
    `${CONTENT}/optimize_image`, { image_gcs_path: imageGcsPath });

// Royalty-free tracks available to replace_audio. Each `name` is the exact audio_name payload
// (e.g. "royalty_free_music/Cocktails - Underbelly & Ty Mayer.mp3"); `url` is a same-origin, auth-gated
// path to stream the track (the audio bucket is private, so it's not a public GCS URL — fetch it with
// getAudioTrackPreview, not a plain <audio src>). Powers the audio picker.
export const getAudioTracks = () =>
  api.get<{ name: string; url: string }[]>(`${CONTENT}/audio_tracks`);

// Fetches a track's bytes (via getBlob, so the Firebase auth header rides along) as an object URL
// the caller can hand to an <audio> element. Caller owns revoking it (URL.revokeObjectURL) when done.
export const getAudioTrackPreview = async (url: string) =>
  URL.createObjectURL(await getBlob(url));

type MusicDetectionApiResult = {
  status: string;
  provider?: string;
  matches?: Array<Record<string, unknown>>;
  reason?: string;
  error_code?: string;
  detail?: string;  // AudD's own error message, when status is "unavailable"
  checked_at?: string;
};

/** SOC-284: run music detection (idempotent; skips terminal statuses unless force). */
export const runMusicDetection = (acId: number, opts?: { force?: boolean }) => {
  const q = opts?.force ? "?force=true" : "";
  return api.post<MusicDetectionApiResult>(
    `${CONTENT}/advertisers_content/${acId}/music-detection${q}`,
    {},
  );
};

/** SOC-284: force re-run music detection on a content row (AudD via socialiq-content). */
export const rescanMusicDetection = (acId: number) =>
  runMusicDetection(acId, { force: true });

export type VideoOptimizeApiResult = {
  status: "not_requested" | "queued" | "processing" | "optimized" | "failed";
  job_id?: string | null;
  requested_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  error_code?: string | null;
  original_size_kb?: number | null;
  output_size_kb?: number | null;
  savings_pct?: number | null;
};

/** Claim + publish the video-optimize job (idempotent — see socialiq_content/core/video_optimize). */
export const triggerVideoOptimize = (acId: number, opts?: { force?: boolean }) => {
  const q = opts?.force ? "?force=true" : "";
  return api.post<VideoOptimizeApiResult>(
    `${CONTENT}/advertisers_content/${acId}/video-optimize${q}`,
    {},
  );
};

/** Persisted video-optimize status for one content row. */
export const getVideoOptimizeStatus = (acId: number) =>
  api.get<VideoOptimizeApiResult>(`${CONTENT}/advertisers_content/${acId}/video-optimize`);

// Native: seasonal_dates is a socialiq-content CRUD resource (PK = season, see migration 005).
// The DATE columns come back as ISO datetimes (…T00:00:00); slice to YYYY-MM-DD for the date inputs.
// Wrapped as { data } to match the shape the Content page already consumes.
export const getSeasonalDates = () =>
  api.get<{ season: string; start_date: string; end_date: string }[]>(`${CONTENT}/seasonal_dates?limit=500`)
    .then(rows => ({
      data: rows.map(r => ({
        season: r.season,
        start_date: String(r.start_date ?? "").slice(0, 10),
        end_date: String(r.end_date ?? "").slice(0, 10),
      })),
    }));

// Upsert by season (the PK): try PUT (edit an existing window); if the season doesn't exist yet the
// content service replies 404, so fall back to POST (create). One call from the UI's onSave either way.
export const upsertSeasonalDate = (season: string, startDate: string, endDate: string) => {
  const body = { season, start_date: startDate, end_date: endDate };
  return api.put<unknown>(`${CONTENT}/seasonal_dates/${encodeURIComponent(season)}`, body)
    .catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) return api.post<unknown>(`${CONTENT}/seasonal_dates`, body);
      throw e;
    });
};

export const deleteSeasonalDate = (season: string, _startDate?: string, _endDate?: string) =>
  api.delete<void>(`${CONTENT}/seasonal_dates/${encodeURIComponent(season)}`);

// ── Chat (Ask Data / Cortex) ──────────────────────────────────────────────────

// Native: socialiq-ai Ask Data (Gemini Conversational Analytics over BigQuery). Conversation state
// lives in Google, keyed by conversation_id. Returns 503 if the BQ Data Agent isn't configured yet.
export const startConversation = (conversationId?: string) =>
  api.post<{ conversation_id: string }>(
    `${AI}/ask_data/start`, conversationId ? { conversation_id: conversationId } : {});

export const askAgent = (conversationId: string, question: string) =>
  api.post<{
    answer: string; sql?: string | null; columns?: string[] | null;
    rows?: Record<string, unknown>[] | null; has_chart: boolean;
    vega_config?: Record<string, unknown> | null; thoughts?: string[];
  }>(`${AI}/ask_data/ask`, { conversation_id: conversationId, question });

// End deletes the Google-side conversation. Cleanup only — swallow any failure so teardown
// (unmount, navigate away) never throws.
export const endConversation = (conversationId: string) =>
  api.post<{ status: string; conversation_id: string }>(`${AI}/ask_data/end`, { conversation_id: conversationId })
    .catch(() => ({ status: "ended", conversation_id: conversationId }));

// ── Performance (partner metrics) — live via socialiq-bqv named queries ─────────
// bqv named queries take a_id as an integer + optional date range; results are bare arrays
// (PerformanceView's extractRows handles both array and {data:[]} shapes).

export interface MetricsRow {
  revenue_source: string;
  total_earnings: number;
  total_intended_earnings: number;
  total_gmv: number;
  total_clicks: number;
  total_ad_costs: number;
  total_profits: number;
  socialiq_profit: number;
  total_bonuses: number;
  socialiq_bonuses: number;
  creator_bonuses: number;
  total_commissionable_gmv: number;
  cc_earnings?: number;
}

// bqv returns rows shaped { source }; PerformanceView expects { data: [{ source, value }] }.
export const getOrganicSources = () =>
  api.post<{ source: string }[]>(`${BQV}/queries/getOrganicSources`, {}).then(rows => ({
    data: rows.map(r => ({ source: r.source, value: r.source })),
  }));

export const getPartnerMetrics = (
  aId: string, startDate: string, endDate: string, organicSource?: string,
) =>
  api.post<unknown>(`${BQV}/queries/getPartnerMetrics`, {
    a_id: Number(aId),
    start_date: startDate,
    end_date: endDate,
    ...(organicSource ? { organic_source: organicSource } : {}),
  });

export const getPartnerMetricsAmazon = (aId: string, startDate: string, endDate: string) =>
  api.post<unknown>(`${BQV}/queries/getPartnerMetricsAmazon`, {
    a_id: Number(aId),
    start_date: startDate,
    end_date: endDate,
  });

// ── Invoices tab: ALL-creators rollups (unscoped — no a_id filter) ──────────────
// Same fields as MetricsRow, plus the creator identity, grouped by (a_id, revenue_source) instead
// of scoped to one caller. Used for the Invoices table (one row per creator); the per-creator
// drill-down reuses getPartnerMetrics/getPartnerMetricsAmazon/getImpactBreakdown above instead.
export interface MetricsAllRow extends MetricsRow {
  a_id: number | null;
  advertisername: string | null;
}

export const getPartnerMetricsAll = (startDate: string, endDate: string) =>
  api.post<MetricsAllRow[]>(`${BQV}/queries/getPartnerMetricsAll`, {
    start_date: startDate, end_date: endDate,
  });

export const getPartnerMetricsAmazonAll = (startDate: string, endDate: string) =>
  api.post<MetricsAllRow[]>(`${BQV}/queries/getPartnerMetricsAmazonAll`, {
    start_date: startDate, end_date: endDate,
  });

// ── BigQuery Viewer (socialiq-bqv) — live via named queries ─────────────────────

export interface AdsPerformanceRow {
  ad_id: string | null;
  total_spend: number | null;
  total_revenue: number | null;
  roas: number | null;
  total_clicks: number | null;
  total_conversions: number | null;
  total_impressions: number | null;
  ctr: number | null;
  conversion_rate: number | null;
}

export const getAdsPerformance = () =>
  api.post<AdsPerformanceRow[]>(`${BQV}/queries/getAdsPerformance`, {});

/** Per content asset (ac_id): launch count, currently-active-ad count, last-launched, and the Meta
 *  ad remote_ids (comma-joined) — the MySQL half of the LauncherView content-card enrichment. */
export const getAdsStatus = () =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAdsStatus`, {});

export interface AdsTimeseriesRow {
  date: string | null;
  spend: number | null;
  revenue: number | null;
  intended_payout: number | null;
  clicks: number | null;
  impressions: number | null;
  conversions: number | null;
}

/** `startDate`/`endDate` bound the series to a window (both or neither — the backend ignores one
 *  on its own); `rowLimit` raises the day cap above its 60-day default so a window longer than
 *  that doesn't come back truncated to its most recent days. Omit all three to get the entity's
 *  last 60 reported days, which is what the callers with no date UI want. */
export const getAdsTimeseries = (params: {
  adsetId?: string; adId?: string;
  startDate?: string; endDate?: string; rowLimit?: number;
}) =>
  api.post<AdsTimeseriesRow[]>(`${BQV}/queries/getAdsTimeseries`, {
    ...(params.adsetId ? { adset_id: params.adsetId } : {}),
    ...(params.adId ? { ad_id: params.adId } : {}),
    ...(params.startDate && params.endDate
      ? { start_date: params.startDate, end_date: params.endDate } : {}),
    ...(params.rowLimit ? { row_limit: params.rowLimit } : {}),
  });

// Unified bonus progress (bqv getBonusProgress) — one endpoint for every merchant's bonus
// tiers. One row per creator; `tiers` is a flat array of entries, each self-contained: it
// carries its own period (year/month) and that period's total_gmv/organic_gmv, so any tier
// percentage is simply total_gmv / tier_gmv, computed here on the fly (nothing pre-derived).
// merchant is case-insensitive; only "amazon" and "walmart" are supported, each with its own
// GMV methodology (see socialiq_bqv/queries/bonus.py) — any other value returns [].
// Optional fromYear/fromMonth/toYear/toMonth bound the periods
// (months clamp to [1,12], missing years inferred, capped at the current PST month); the UI
// omits them and gets everything up to the current month.
export interface BonusTierEntry {
  year: number;
  month: number;
  tier: number;                 // 1-based rank within its period, ascending goal
  tier_gmv: number | null;      // cumulative month-GMV goal of this tier
  tier_bonus: number | null;    // payout at this tier, USD
  total_gmv: number;            // the creator's month-to-date GMV for this period
  organic_gmv: number | null;   // null for merchants without the concept
  total_profit: number | null;  // month-to-date payout - spend; Walmart = whole-Impact profit, not Walmart-only
}

export interface BonusProgressRow {
  merchant: string;
  a_id: string;
  creator: string;
  tiers: BonusTierEntry[];
}

export const getBonusProgress = (merchant: string) =>
  api.post<BonusProgressRow[]>(`${BQV}/queries/getBonusProgress`, { merchant });

// NOTE: getPartnerIncrementalBonus (bqv) is no longer called from the frontend — the Invoices tab
// now reads socialiq_bonuses/creator_bonuses off MetricsRow (getPartnerMetricsAll/Amazon(All))
// instead (see InvoicesView.tsx). The backend query itself is still live and used by
// socialiq_invoicing/core/dues.py for the actual billed amount — do not remove it there.

export interface ImpactBreakdownRow {
  a_id: number | null;
  dt_pst: string | null;
  tracking_id: string | null;
  revenue_source: string | null;
  advertisername: string | null;
  publishername: string | null;
  campaign_name: string | null;
  spend: number | null;
  payout: number | null;
  redirector_cost: number | null;
  redirector_profit: number | null;
  // Used by the Invoices tab's due-calculation (core/dues.py's frontend mirror) — not previously
  // typed here since no consumer needed them yet.
  redirector_cost_with_fee: number | null;
  cmbs: number | null;
  mmbs: number | null;
}

// Per-creator granular Impact rows. Date filter applies only when both dates are set.
export const getImpactBreakdown = (aId: string, startDate?: string, endDate?: string) =>
  api.post<ImpactBreakdownRow[]>(`${BQV}/queries/getImpactBreakdown`, {
    a_id: Number(aId),
    ...(startDate && endDate ? { start_date: startDate, end_date: endDate } : {}),
  });

export interface DashboardRow {
  dt_pst: string | null;
  tracking_id: string | null;
  revenue_source: string | null;
  advertisername: string | null;
  publishername: string | null;
  merchant: string | null;
  publisher_type: string | null;
  spend: number | null;
  payout: number | null;
  gmv: number | null;
}

// Granular non-organic revenue rows (both platforms). Always pass a date range to bound the scan.
export const getDashboardData = (startDate: string, endDate: string) =>
  api.post<DashboardRow[]>(`${BQV}/queries/getDashboardData`, {
    start_date: startDate,
    end_date: endDate,
  });

// ── socialiq-sql named queries ──────────────────────────────────────────────────

/** Run history — replaces the (now unregistered) autolauncher_executions CRUD resource. */
export const getAutolauncherExecutions = () =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAutolauncherExecutions`, {});

/** Templates + last-run rollup. The raw autolauncher_template CRUD row has NO run_count / status /
 *  last_run / selected_creators (rollup fields) and no n_ads (now per-creator), so reading templates
 *  via CRUD left those undefined (e.g. Total Ads Out = n_ads*run_count = NaN). This named query
 *  returns the full config PLUS the rollup + selected_creators (a JSON array of {a_id, n_ads}). */
export const getAutolauncherTemplates = () =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAutolauncherTemplates`, {});

/** Distinct Impact programs (id + name) for the Recipe "Program / Merchant" picker.
 *  Reads impact_programs via the named query. The old CRUD resource pointed at impact_accounts —
 *  a credentials table with no program_id/label — so the dropdown rendered a long list of blank
 *  options. This query returns exactly program_id (campaign_id) + label (campaign_name). No params. */
export const getImpactPrograms = () =>
  api.post<{ program_id: number; label: string }[]>(`${SQL}/queries/getImpactProgramsAll`, {});

/** Meta ad accounts (id + name) for the Recipe "Account ID" picker — the port of Retool's
 *  getAccounts. Returns { account_id: fb_accounts.fa_id, label: fb_accounts.name }. No params. */
export const getAdAccounts = () =>
  api.post<{ account_id: number; label: string }[]>(`${SQL}/queries/getAdAccounts`, {});

/** Amazon-tag counts per creator (total + unallocated) for the LauncherView "tags available"
 *  indicator — the aggregate the monolith bound to LauncherView.amazonTags. The raw amazon_tags CRUD
 *  list does NOT carry these aggregate columns, so the indicator must read this query. No params. */
export const getAmazonTagsAvailable = () =>
  api.post<{ a_id: number; total_tags: number; available_tags: number }[]>(
    `${SQL}/queries/getAmazonTagsAvailablePerCreator`, {});

/** Impact programs a specific advertiser is enrolled in, for the URL Creator tool's "Impact Program"
 *  picker — unlike getImpactPrograms (unfiltered, kept as-is for the Wizard), this genuinely filters
 *  by a_id server-side. Returns program_id (campaign_id) + label (advertiser_name) + program_url
 *  (advertiser_url, used to ground the tracking link). */
export const getImpactProgramsByAdvertiser = (aId: string | number) =>
  api.post<{ program_id: number; label: string; program_url: string | null }[]>(
    `${SQL}/queries/getImpactProgramsByAdvertiser`, { a_id: Number(aId) });

/** Active media buyers (id + name) for the automation-rule subscription member picker — the port of
 *  the Retool getTeam member list ({ b_id, name }). No params. */
export const getMediaBuyers = () =>
  api.post<{ b_id: number; name: string }[]>(`${SQL}/queries/getMediaBuyers`, {});

/** A creator's merchant names (their Impact programs + a literal 'Amazon' when they have a store) for
 *  the content-editor merchant dropdown — the port of Retool getMerchants(merchantCreatorId=a_id). */
export const getCreatorMerchants = (aId: string | number) =>
  api.post<{ name: string }[]>(`${SQL}/queries/getCreatorMerchants`, { a_id: Number(aId) });

// ── Merchant URL cleaning (socialiq-content → socialiq-marketplace) ───────────

/** Why a link was not cleaned. `warning` still leaves a usable link (the merchant has no cleaning
 *  rule, or isn't one we carry); `error` means the input is not a link at all. */
export interface CleanUrlIssue { type: string; title: string; detail?: string }

/** One entry per input URL, in request order. Exactly one of cleaned_url / warning / error is set. */
export interface CleanUrlResult {
  url: string;
  cleaned_url?: string;
  warning?: CleanUrlIssue;
  error?: CleanUrlIssue;
}

/** Strips tracking params using the merchant's url_cleaning_strategy. socialiq-content forwards the
 *  list to marketplace with its own X-Api-Key, so no key ships in the bundle; a 502/503 from it means
 *  marketplace failed or this environment has no marketplace_url yet. */
export const cleanMerchantUrls = (urls: string[]) =>
  api.post<{ results: CleanUrlResult[] }>(`${CONTENT}/clean-urls`, { urls });

/** Create/update an autolauncher template + sync its per-creator rows in one transaction.
 *  A "recipe" spans two tables — autolauncher_template (config) and autolauncher_creator_template
 *  (the selected_creators a_id/n_ads rows). The generic CRUD create can't accept selected_creators
 *  (extra_forbidden) or write the join table, so it's done via this composite mutation (mirrors the
 *  monolith's save_template). Pass the whole wizard form; UI-only fields are ignored server-side.
 *  Omit `id` to create; include it to update. */
export const saveAutolauncherTemplate = (body: Record<string, unknown>) =>
  api.post<{ success: boolean; at_id: number; message: string }>(
    `${SQL}/mutations/saveAutolauncherTemplate`, body);

/** Automation rule executions — no named query yet, so read the MySQL table via /custom. */
/** Automation-rule executions (HITL Executions tab) — via socialiq-automation, which returns each
 *  row's `simulation` PARSED to a list (the old /custom read returned it as a JSON string, crashing
 *  the tab). Optional arId filters server-side per rule (replaces the client-side filter + the
 *  arbitrary global LIMIT 100). */
export const getAutomationExecutions = (arId?: number, limit = 100) =>
  api.post<{ executions: Record<string, unknown>[] }>(`${AUTOMATION}/get_executions`,
    { ar_id: arId ?? null, limit }).then(r => r.executions ?? []);

/** Automation-rule subscribers with rule_name + buyer_name (LEFT JOINs, deleted=0). Optional arId
 *  filters to one rule. Replaces the generic CRUD list, which returned raw rows (no names → the UI
 *  showed "Buyer #<b_id>") and leaked the internal `deleted` column. */
export const getAutomationSubs = (arId?: number) =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAutomationSubs`, { ar_id: arId ?? null });

// ── Performance (ad-ops console) — ported from the Retool autolauncher view ─────
// Per-ad / per-adset operational rows + drill-downs. Reads only; the Meta-write
// actions (bulk budget/status, clone) are still legacy-only (see PerformanceView).

// Structurally compatible with PerformanceView.tsx's TimeFilterSpec (duck-typed — actions.ts
// stays independent of the view module; only the shape needs to match what the backend expects).
export interface TimeFilterSpecPayload { id: string; metric: string; type: string; days_ago: number }

// Meta-Impact rows — server-side filtered by date + status, plus optional dynamic time-filter
// columns (see socialiq_bqv/queries/optimization.py's "Time filters" section).
export const getFbImpactStatsRollup = (startDate: string, endDate: string, status: string, timeFilters?: TimeFilterSpecPayload[]) =>
  api.post<PerfRow[]>(`${BQV}/queries/getFbImpactStatsRollup`, {
    start_date: startDate, end_date: endDate, status, time_filters: timeFilters ?? [],
  });

// Meta-Amazon adset-level rollup rows — server-side filtered by date + status (was called with no
// params, so the base numbers were silently all-time instead of the selected range; the backend
// query already supported these — FbAmazonStatsByAdsetParams inherits FbImpactStatsParams' WHERE
// clause — this was purely a frontend wiring gap), plus optional time-filter columns.
export const getFbAmazonStatsByAdsetRollup = (startDate: string, endDate: string, status: string, timeFilters?: TimeFilterSpecPayload[]) =>
  api.post<PerfRow[]>(`${BQV}/queries/getFbAmazonStatsByAdsetRollup`, {
    start_date: startDate, end_date: endDate, status, time_filters: timeFilters ?? [],
  });

// Meta-Amazon ad-level rows (for adset drill-down / preview / ad-level search) — same gap, same
// fix, plus optional time-filter columns.
export const getFbAmazonStatsRollup = (startDate: string, endDate: string, status: string, timeFilters?: TimeFilterSpecPayload[]) =>
  api.post<PerfRow[]>(`${BQV}/queries/getFbAmazonStatsRollup`, {
    start_date: startDate, end_date: endDate, status, time_filters: timeFilters ?? [],
  });

// ── Social Brand internal report (CortexV2 /internal-report, social_direct_internal role) ─────
// socialiq-bqv serves it to staff holding the role only (socialiq_bqv/core/staff.py), and drops the
// internal columns for anyone else; hiding the menu entry (auth/roles.ts) is only a courtesy.
// Local demo only: `npm run dev` with VITE_SD_REPORT_SOURCE=fixtures in .env.local answers from
// fictional fixtures shaped like bqv's response (views/internalReport/fixtureServer.ts).
// import.meta.env.DEV is false in every `vite build`, so a deployed bundle never contains them.
export const SD_INTERNAL_SOURCE: "fixtures" | "bqv" =
  import.meta.env.DEV && (import.meta.env.VITE_SD_REPORT_SOURCE as string | undefined) === "fixtures"
    ? "fixtures" : "bqv";

export async function getSocialDirectInternalReport(req: InternalReportRequest, opts?: { as?: "client" }): Promise<InternalReportResponse> {
  if (import.meta.env.DEV && SD_INTERNAL_SOURCE === "fixtures") {
    const { serve } = await import("../views/internalReport/fixtureServer");
    // `opts.as` is the fixtures preview's "open it as a client" (?as=client): the response a caller without the role gets
    return new Promise((res, rej) => setTimeout(() => { try { res(serve(req, opts?.as === "client" ? { audience: "client" } : undefined)); } catch (e) { rej(e); } }, 120));
  }
  return api.post<InternalReportResponse>(`${BQV}/queries/getSocialDirectInternalReport`, req);
}

// The internal report's refreshes (staff only; socialiq-bqv queries/social_direct_refresh.py). The page's "Quick refresh" starts the
// `live` schedule (the internal report alone, inside its live window; reason `staff`) and its "Full refresh" the checked 15-minute
// refresh of the mart and the internal report (`kind: "full"`); neither asks the decider. A start without `kind` asks bqv's decider,
// which answers `up_to_date` (nothing is started: `state: "up_to_date"`, no run), `quick` or `full`.
// `checked` (next `full`) is only what a run of the retired check schedule wrote. Only enums, run ids and advertiser names come
// back, never a message from Google or BigQuery. The fixtures preview answers locally: it has no bqv.
export type SdRefreshKind = "full" | "live";
export type SdRefreshState = "pending" | "running" | "succeeded" | "skipped" | "checked" | "failed" | "not_run" | "waiting" | "up_to_date";
export type SdRefreshDecision = "up_to_date" | "quick" | "full";
export interface SdRefreshStart {
  run: string | null; kind: SdRefreshKind | null; state: SdRefreshState | null; started: boolean; expect_s?: number;
  next_full_at?: string | null; reason?: string;
  // a Motion save refused because a run was busy: ask again with this `since` and exactly one quick run follows (`covered` ends it)
  since?: string; retry_after_s?: number;
  // what the decider said and why (enums), and how long it took; absent from the runs bqv returns as already under way
  decision?: SdRefreshDecision; reasons?: string[]; decided_ms?: number;
}
export interface SdRefreshStatus {
  run: string; kind?: SdRefreshKind; state: SdRefreshState; mart_status?: "succeeded" | "skipped" | "failed" | null;
  finished_at: string | null; next?: "full" | null;
}
// "Motion refresh pending": the advertisers with a Motion change newer than what the internal report shows for them.
export interface SdPending { advertiser: string; since: string | null; latest: string | null }
// Only what the page reads (bqv serves nothing more): which kind of refresh runs, not its id or its clock.
export interface SdRefreshActivity { kind: SdRefreshKind }
export interface SdRefreshStateResponse {
  available: boolean; pending?: SdPending[]; unplaced?: boolean; shown_at?: string | null;
  full_failing?: boolean; live_failed?: boolean; active: SdRefreshActivity | null; next_full_at: string | null;
}

const fixtures = () => import.meta.env.DEV && SD_INTERNAL_SOURCE === "fixtures";
const later = <T,>(ms: number, v: () => T) => new Promise<T>(res => setTimeout(() => res(v()), ms));
const laterOrThrow = <T,>(ms: number, v: () => T) =>
  new Promise<T>((res, rej) => setTimeout(() => { try { res(v()); } catch (e) { rej(e); } }, ms));

// The internal report's Copy (views/internalReport/copy.ts): socialiq-launcher's /clone-brand, and /clone/parents for where a single ad
// may go, exactly as Meta-Brands calls them; the launcher checks performance_write on the copy. The fixtures preview answers from
// views/internalReport/fixtureCopy.ts and creates nothing.
export async function copyFromReport(form: Parameters<typeof cloneAds>[0]): Promise<CopyAnswer> {
  if (fixtures()) {
    const { fixtureCopy } = await import("../views/internalReport/fixtureCopy");
    return laterOrThrow(400, () => fixtureCopy(form));
  }
  return cloneBrandAds(form);
}

export async function copyParentsFromReport(ad_id: string): ReturnType<typeof getCloneParents> {
  if (fixtures()) {
    const { fixtureCopyParents } = await import("../views/internalReport/fixtureCopy");
    return laterOrThrow(300, () => fixtureCopyParents(ad_id));
  }
  return getCloneParents(ad_id);
}

export async function startSocialDirectRefresh(opts?: { kind?: "full" }): Promise<SdRefreshStart> {
  if (fixtures()) {
    const { fixtureRefresh } = await import("../views/internalReport/fixtureServer");
    return fixtureRefresh.start(opts?.kind);
  }
  return api.post<SdRefreshStart>(`${BQV}/queries/startSocialDirectRefresh`, opts?.kind ? { kind: opts.kind } : {});
}

export async function startSocialDirectLiveRefresh(reason: "motion_save" | "pending" | "staff", since?: string): Promise<SdRefreshStart> {
  if (fixtures()) {
    const { fixtureRefresh } = await import("../views/internalReport/fixtureServer");
    return fixtureRefresh.start("live");
  }
  return api.post<SdRefreshStart>(`${BQV}/queries/startSocialDirectLiveRefresh`, since ? { reason, since } : { reason });
}

export async function getSocialDirectRefreshStatus(run: string, kind: SdRefreshKind = "full"): Promise<SdRefreshStatus> {
  if (fixtures()) {
    const { fixtureRefresh } = await import("../views/internalReport/fixtureServer");
    return later(800, () => fixtureRefresh.status(run, kind));
  }
  return api.post<SdRefreshStatus>(`${BQV}/queries/getSocialDirectRefreshStatus`, { run, kind });
}

export async function getSocialDirectRefreshState(): Promise<SdRefreshStateResponse> {
  if (fixtures()) {
    const { fixtureRefresh } = await import("../views/internalReport/fixtureServer");
    return later(120, () => fixtureRefresh.state());
  }
  return api.post<SdRefreshStateResponse>(`${BQV}/queries/getSocialDirectRefreshState`, {});
}

// The internal report's ad preview (Ads tab only) reads the same creative Meta-Brands' does: socialiq-sql getAdCreative, at most
// one row, none where Cortex holds no row for the ad. The fixtures preview answers locally, from fictional creatives.
export async function getSdAdCreative(adId: string): Promise<Record<string, unknown>[]> {
  if (fixtures()) {
    const { fixtureCreative } = await import("../views/internalReport/fixtureEntities");
    return later(150, () => fixtureCreative(adId));
  }
  return getAdCreative(adId);
}

// After a Motion save (hooks/useMotion.ts): ask for the live refresh, so the internal report shows the change in about half a
// minute. Fire and forget: the save has already succeeded, and the server's own refresh message, the report's pending mark and the
// 15-minute refresh cover a nudge that does not arrive (a person without the internal report's role, a closed tab, an environment
// without the schedule). A nudge bqv refuses because a run is busy or the 15-minute refresh is about to start is asked again every
// `retry_after_s` with the `since` bqv answered, until the save is `covered` (a run that started after it) or started: so a save made
// just after a refresh began is followed by exactly one quick run, never forgotten (the race found on dev, 2026-10-15).
export const NUDGE_MAX_ATTEMPTS = 24;
const NUDGE_RETRY: ReadonlySet<string> = new Set(["running", "too_soon", "full_due"]);
export function nudgeSocialDirectLiveRefresh(): void {
  if (fixtures()) return;
  const attempt = (n: number, since?: string) => {
    startSocialDirectLiveRefresh("motion_save", since).then(r => {
      if (r.started || !r.reason || !NUDGE_RETRY.has(r.reason) || n + 1 >= NUDGE_MAX_ATTEMPTS) return;
      setTimeout(() => attempt(n + 1, r.since ?? since), Math.max(5, r.retry_after_s ?? 15) * 1000);
    }, () => { /* best effort: see above */ });
  };
  attempt(0);
}

// ── Time-filter presets (socialiq-sql mutations) ──────────────────────────────
// owner_email is NEVER sent — the backend derives it from the authenticated caller (see
// socialiq_sql/core/mutation.py). These three map 1:1 onto PerformanceView's FiltersModal.
export const saveTimeFilterPreset = (payload: {
  view: string; name: string; spec: { time_filters: TimeFilterSpecPayload[]; column_filters: unknown[] };
  visibility?: "private" | "public";
}) => api.post<{ message: string }>(`${SQL}/mutations/saveTimeFilterPreset`, payload);

export const listTimeFilterPresets = (view: string) =>
  api.post<Record<string, unknown>[]>(`${SQL}/mutations/listTimeFilterPresets`, { view });

export const deleteTimeFilterPreset = (id: number) =>
  api.post<{ message: string }>(`${SQL}/mutations/deleteTimeFilterPreset`, { id });

// Adset changelog (Insights drawer).
export const getAdsetChangelog = (adsetId: string) =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAdsetChangelog`, { adset_id: adsetId });

// Ad creative (Ad Preview modal).
export const getAdCreative = (adId: string) =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getAdCreative`, { ad_id: adId });

// Content Performance grid (SOC-582) — per ad: matched content asset, landing URL, network, launch
// date. Merge onto a bqv performance rollup (getFbImpactStatsRollup / getFbAmazonStatsRollup)
// client-side by ad_id. adIds is REQUIRED (the ad_ids already loaded from that rollup) — the query
// scopes to exactly those ads server-side; a no-param full facebook_ads scan timed out on real data.
export interface AdsContentLinksRow {
  ad_id: string | null;
  ac_id: number | null;
  content_url: string | null;
  media_type: string | null;
  landing_url: string | null;
  network: string | null;
  launch_date: string | null;
  a_id: number | null;
  niche: string | null;
}

export const getAdsContentLinks = (adIds: string[]) =>
  api.post<AdsContentLinksRow[]>(`${SQL}/queries/getAdsContentLinks`, { ad_ids: adIds });

// Meta-Brands rows — MySQL (socialiq-sql), scoped by brand a_id, server-filtered by date + status.
// This is the one Performance-View stats read that is MySQL (fb_ads_insights_adnet), not BigQuery, so
// it targets socialiq-sql, not bqv. Returns {items:[...]} (custom router) → unwrap to the row list.
//
// Then overlay the Impact/SA360 affiliate revenue the monolith applied post-query (_apply_external_revenue):
// the base sql query gives the Meta-pixel numbers with total_payout=NULL; here we merge in the external
// commission for Impact-tracked brands from the bqv overlay queries (which already exist), staying within
// the service boundary (sql never touches BigQuery — the FRONTEND composes the two). Best-effort: an
// overlay failure leaves the correct base numbers intact.
function _applyExternalRevenue(
  row: PerfRow, impact?: Record<string, unknown>, sa360?: Record<string, unknown>,
): PerfRow {
  const impConv = Number(impact?.impact_conversions ?? 0);
  const impPayout = Number(impact?.impact_payout ?? 0);
  const impGmv = Number(impact?.impact_gmv ?? 0);
  const saConv = Number(sa360?.sa360_conversions ?? 0);
  const saGmv = Number(sa360?.sa360_payout ?? 0);
  const extConv = impConv + saConv;
  const extGmv = impGmv + saGmv;
  const extPayout = impPayout;                       // affiliate commission is Impact-only
  // If no external source reports anything, keep the FB-pixel numbers untouched (monolith rule).
  if (extConv === 0 && extGmv === 0 && extPayout === 0) return row;
  const spend = Number(row.total_spend ?? 0);
  const clicks = Number(row.total_clicks ?? 0);
  // Derived metrics recompute against GMV ("what the brand earned"); payout is a cost, not revenue.
  return {
    ...row,
    total_gmv: extGmv,
    total_conversions: extConv,
    profit: extGmv - spend,
    total_payout: (extPayout ? extPayout : null) as unknown as number,   // null → renders "—" (monolith)
    roas: (spend ? extGmv / spend : null) as unknown as number,
    conversion_rate: clicks ? extConv / clicks : undefined,
    cpa: extConv ? spend / extConv : undefined,
  };
}

export const getFbBrandsStats = async (
  brandAId: string, startDate: string, endDate: string, status: string,
): Promise<PerfRow[]> => {
  const base = await api.post<{ items: PerfRow[] }>(`${SQL}/getFbBrandsStats`, {
    a_id: Number(brandAId), start_date: startDate, end_date: endDate, status,
  }).then(r => r.items ?? []);
  const adIds = [...new Set(base.map(r => String(r.ad_id)).filter(Boolean))];
  // The overlay queries require a date range; the Meta-Brands Load always supplies one. No rows → skip.
  if (!startDate || !endDate || adIds.length === 0) return base;
  const [impact, sa360] = await Promise.all([
    api.post<Record<string, unknown>[]>(`${BQV}/queries/getImpactConversionsByAdIdRollup`,
      { ad_ids: adIds, start_date: startDate, end_date: endDate }).catch(() => [] as Record<string, unknown>[]),
    api.post<Record<string, unknown>[]>(`${BQV}/queries/getSa360ConversionsByAdIdRollup`,
      { ad_ids: adIds, start_date: startDate, end_date: endDate }).catch(() => [] as Record<string, unknown>[]),
  ]);
  const impactBy = new Map((impact ?? []).map(r => [String(r.ad_id), r]));
  const sa360By = new Map((sa360 ?? []).map(r => [String(r.ad_id), r]));
  return base.map(r => _applyExternalRevenue(r, impactBy.get(String(r.ad_id)), sa360By.get(String(r.ad_id))));
};

/**
 * BRAND BREAKDOWN (bqv) — the same brands, at whichever level the operator picks.
 *
 * WHY A SECOND SOURCE ALONGSIDE getFbBrandsStats. That one is MySQL (socialiq-sql) and can only ever
 * see Meta's own pixel, because the clients' real sources of truth — GA4, Fospha, Northbeam, SA360,
 * and the podcast ledger — live only in BigQuery. This one runs in bqv, which owns those reads, and
 * returns whichever truth each brand actually has.
 *
 * READ `revenue_source` BEFORE READING `revenue`. It is not decoration:
 *   external / podcast / pixel   a MEASURED number, from that brand's own source
 *   *_allocated                  the level above's number, split by spend share (adset and ad have no
 *                                revenue of their own anywhere in the warehouse)
 *   client_unmatched / none      spend with no revenue attached — shown, never hidden as zero
 * Totals reconcile across levels: spend is identical at all four and revenue within rounding.
 */
export type BrandLevel = "advertiser" | "campaign" | "adset" | "ad";

export interface BrandBreakdownRow {
  a_id: string; advertiser_name: string;
  entity_id: string; parent_id: string | null; entity_name: string;
  spend: number; impressions: number; clicks: number; pixel_conversions: number;
  revenue: number | null; revenue_source: string; roas: number | null;
  // spend MINUS attributed revenue — positive means the spend was not earned back. Named for what
  // it is rather than "profit", which would invert the sign the rest of the UI expects.
  profit_inverse: number | null;
  date_start?: string;   // time-series only
}

export const getBrandBreakdown = (
  level: BrandLevel, startDate: string, endDate: string,
  aId?: number | null, parentId?: string | null,
) => api.post<BrandBreakdownRow[]>(`${BQV}/queries/getBrandBreakdown`, {
  level, start_date: startDate, end_date: endDate,
  a_id: aId ?? null, parent_id: parentId ?? null,
});

/** Where each day's Amazon earnings were attributed (SOC-218 audit trail).
 *
 * One row per (ship day -> order cohort) hand-off. Sum a ship_day and you get what Amazon reported
 * that day; sum a cohort_day and you get what the tab credits that day. An EMPTY result for recent
 * ship days means the source hasn't reported shipments yet — not that the days performed badly. */
export interface AmazonFlowRow {
  ship_day: string;
  cohort_day: string;
  lag_days: number;
  gmv: number | null;
  /** Units ORDERED on the cohort day — hard count, not an estimate. */
  cohort_orders: number | null;
  /** GMV ordered on the cohort day. */
  cohort_ordered_revenue: number | null;
  /** Amazon's commission on the shipment — the figure that reconciles against their console. */
  base_payout: number | null;
  /** Creator Connections on the same shipment. FREQUENTLY NEGATIVE (reversals), which is why it
   *  must be shown separately: netted in silently it makes commission look wrong. */
  connections_payout: number | null;
  /** base_payout + connections_payout — what the model actually credits. */
  payout: number | null;
}

export const getAmazonAttributionFlows = (
  params: { adsetId?: string | null; tag?: string | null; days?: number },
) => api.post<AmazonFlowRow[]>(`${BQV}/queries/getAmazonAttributionFlows`, {
  adset_id: params.adsetId ?? null,
  tag: params.tag ?? null,
  days: params.days ?? 14,
});

/** The same rows, one per day — the series behind whichever row was selected.
 *
 * PASS `entityId`. Unscoped, an ad-level series is ~900 ads x ~50 days, which exceeds bqv's 10k row
 * cap — and the cap returns a PREFIX, so the chart silently loses the end of the date range and
 * reads as an ad that stopped spending. Scoped, it is one row per day. */
export const getBrandTimeSeries = (
  level: BrandLevel, startDate: string, endDate: string,
  aId?: number | null, parentId?: string | null, entityId?: string | null,
) => api.post<BrandBreakdownRow[]>(`${BQV}/queries/getBrandTimeSeries`, {
  level, start_date: startDate, end_date: endDate,
  a_id: aId ?? null, parent_id: parentId ?? null, entity_id: entityId ?? null,
});

// Meta-Brands picker source — direct-advertiser brands + their FB ad-account linkage (getBrandAccounts
// named query). has_account=0 brands render disabled in the picker (no insights yet). Coerce a_id →
// string and has_account → boolean to match the BrandAccount type.
export const getBrandAccounts = () =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getBrandAccounts`, {}).then(rows =>
    rows.map(r => ({
      a_id: String(r.a_id ?? ""),
      name: String(r.name ?? ""),
      fa_id: (r.fa_id ?? null) as number | null,
      remote_id: (r.remote_id ?? null) as string | null,
      fa_status: (r.fa_status ?? null) as string | null,
      has_account: Boolean(Number(r.has_account ?? 0)),
    })) as BrandAccount[]);

// Creators (indirect advertisers) WITH their enrolled Impact program ids + Amazon flag. The
// getCreators named query returns program_ids as a comma-joined string; split it to an array so the
// Wizard's "enrolled creators" count + the program filter work (G7). This is the correct monolith
// source (is_direct_advertiser=0) — it replaces the generic advertisers CRUD list, which returned no
// program_ids and included brands. a_id is coerced to string (the views compare String(a_id)).
export type CreatorOption = {
  a_id: string; name: string; active?: number; has_amazon?: number; program_ids: string[];
  email?: string;
};
export const getCreators = () =>
  api.post<Record<string, unknown>[]>(`${SQL}/queries/getCreators`, {}).then(rows =>
    rows.map(r => ({
      a_id: String(r.a_id ?? ""),
      name: String(r.name ?? ""),
      active: r.active as number | undefined,
      has_amazon: r.has_amazon as number | undefined,
      email: (r.email ?? undefined) as string | undefined,
      program_ids: typeof r.program_ids === "string" && r.program_ids
        ? (r.program_ids as string).split(",").map(s => s.trim()).filter(Boolean)
        : [],
    })) as CreatorOption[]);

// ── Invoices (finance) — socialiq-invoicing ──────────────────────────────────────
// A creator/month can now have MULTIPLE invoices, one per `InvoiceType` ("boosting"/"bonus"/
// "custom") issued — there is no cap and no duplicate-blocking. issueInvoice's dollar amount
// defaults per type when `amountCents` is omitted (recomputed server-side, never trusted from
// here for the default case — same pattern as socialiq-sql's owner_email handling), but the
// operator can always override it via `amountCents`. A 503 means Stripe isn't configured on the
// service yet (expected until a real test-mode key is added). due_date is the operator-selected
// payment-due date shown on the invoice (YYYY-MM-DD, must not be in the past) — informational
// only. The invoice is created `open` immediately AND EMAILED to the customer right away (Stripe's
// hosted-invoice link — the customer can pay it themselves from that email, not just via
// chargeInvoice below) — issuing is a customer-facing action now, not a quiet internal step;
// voidInvoice is the only undo for a mistaken issue (wrong amount/creator/label), and even that
// doesn't un-send the email, only stops the invoice being payable.

export type InvoiceType = "boosting" | "bonus" | "custom";

export interface IssueInvoiceResult {
  a_id: number;
  year: number;
  month: number;
  invoice_type: InvoiceType;
  label: string;
  amount_cents: number;
  currency: string;
  stripe_invoice_id: string;
  stripe_customer_id: string;
  status: string;
  live_mode: boolean;
  due_date: string;
  /** Adjustment lines applied to this invoice (empty unless it carried past-month adjustments).
   * `amount_cents` above is then the invoice TOTAL: base + these. */
  adjustments?: AppliedAdjustmentResult[];
  /** The operator's custom lines on this invoice, as sent. Part of `amount_cents`. */
  custom_lines?: CustomLineRequest[];
}

// An operator's own line on any invoice type: a fee (positive) or a discount (negative). Charged and
// paid like any other line, but never counted as billing for the month. The server refuses a set of
// lines that would take the invoice total below $0.
export interface CustomLineRequest {
  description: string;
  amount_cents: number;
}

// A signed line settling an EARLIER month's over/undercharge on a Total Balance ("boosting")
// invoice: negative = credit (that month was overcharged), positive = extra charge (undercharged).
// `amount_cents` is what the server APPLIED (a credit larger than the invoice is capped so the total
// never goes below $0 — the rest stays open for a later invoice); `requested_cents` is what was asked.
export interface InvoiceAdjustmentRequest {
  settles_year: number;
  settles_month: number;
  amount_cents: number;
}

export interface AppliedAdjustmentResult extends InvoiceAdjustmentRequest {
  requested_cents: number;
}

export const issueInvoice = (
  aId: string, year: number, month: number, dueDate: string, invoiceType: InvoiceType,
  opts?: { label?: string; amountCents?: number; adjustments?: InvoiceAdjustmentRequest[];
          customLines?: CustomLineRequest[] },
) =>
  api.post<IssueInvoiceResult>(`${INVOICING}/mutations/issueInvoice`, {
    a_id: Number(aId), year, month, due_date: dueDate, invoice_type: invoiceType,
    label: opts?.label ?? null, amount_cents: opts?.amountCents ?? null,
    // Only sent when there are lines — boosting only (the server 422s otherwise), and bulk issue
    // never passes any.
    ...(opts?.adjustments?.length ? { adjustments: opts.adjustments } : {}),
    ...(opts?.customLines?.length ? { custom_lines: opts.customLines } : {}),
  });

// Past months this creator's Total Balance invoice could settle (every month before year/month,
// within the server's lookback, that was invoiced and has a nonzero open difference), plus every
// adjustment already recorded for them. `counted: false` means the carrying invoice was voided or
// is still a draft, so that difference is open again. Balances refresh daily server-side —
// `balance_refreshed_at` says how fresh each month's total_due is.
export interface CarryOverSuggestion {
  settles_month: string; // "YYYY-MM-01"
  total_due_cents: number;
  invoiced_attributed_cents: number;
  open_difference_cents: number;
  suggested_cents: number;
  balance_refreshed_at: string | null;
}

export interface AppliedAdjustmentRecord {
  adjustment_id: number;
  settles_month: string;
  invoice_month: string;
  amount_cents: number;
  stripe_invoice_id: string;
  created_by: string | null;
  created_at: string | null;
  counted: boolean;
}

export interface CarryOverSuggestionsResult {
  suggestions: CarryOverSuggestion[];
  applied_adjustments: AppliedAdjustmentRecord[];
}

export const getCarryOverSuggestions = (aId: string, year: number, month: number) =>
  api.post<CarryOverSuggestionsResult>(`${INVOICING}/queries/getCarryOverSuggestions`, {
    a_id: Number(aId), year, month,
  });

// Per creator, for one month: what is owed now vs. invoiced (adjustments attributed to the month
// they are about) — only creators with a stored balance row for that month.
export interface MonthBalanceRow {
  a_id: number;
  name: string;
  email: string | null;
  total_due_cents: number;
  invoiced_cents: number;
  paid_cents: number;
  invoiced_attributed_cents: number;
  open_difference_cents: number;
  refreshed_at: string | null;
  has_adjustments: boolean;
  exact: boolean;
}

export const listMonthBalances = (year: number, month: number) =>
  api.post<MonthBalanceRow[]>(`${INVOICING}/queries/listMonthBalances`, { year, month });

// Recompute stored month balances now instead of waiting for the daily refresh. 200 even when some
// creators failed — check `failed`/`errors`. `months` are "YYYY-MM"; omit to refresh the recent ones.
export interface RefreshMonthBalancesResult {
  creators: number;
  months_written: number;
  failed: number;
  errors: unknown[];
}

export const refreshMonthBalances = (opts?: { aId?: string; months?: string[] }) =>
  api.post<RefreshMonthBalancesResult>(`${INVOICING}/mutations/refreshMonthBalances`, {
    ...(opts?.aId ? { a_id: Number(opts.aId) } : {}),
    ...(opts?.months ? { months: opts.months } : {}),
  });

// Recovery path: finalize an invoice that's unexpectedly stuck in `draft` (issueInvoice normally
// creates invoices `open` immediately, so this is rarely needed). 409 means it's no longer a
// draft; 404 means no such invoice, or it doesn't belong to this creator. `stripeInvoiceId`
// (from listIssuedInvoices/issueInvoice) picks the specific invoice — a creator/month can have
// more than one now, so this is no longer derivable from year/month alone.
export interface FinalizeInvoiceResult {
  a_id: number;
  stripe_invoice_id: string;
  status: string;
}

export const finalizeInvoice = (aId: string, stripeInvoiceId: string) =>
  api.post<FinalizeInvoiceResult>(`${INVOICING}/mutations/finalizeInvoice`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId,
  });

// Charge an OPEN invoice's customer now, via their payment method on file — the only way an
// invoice is ever actually charged (invoices are issued `open` with collection_method
// "send_invoice", which Stripe never auto-charges on its own). 409 means the invoice isn't open
// (already paid/void, or still an unfinalized draft — see finalizeInvoice). Omitting
// paymentMethodId tries the customer/invoice's default first; if there isn't one, this 422s with
// Stripe's "no default_payment_method set..." message — the caller should then fetch
// listPaymentMethods and retry with a specific one.
export interface ChargeInvoiceResult {
  a_id: number;
  stripe_invoice_id: string;
  status: string;
}

export const chargeInvoice = (aId: string, stripeInvoiceId: string, paymentMethodId?: string) =>
  api.post<ChargeInvoiceResult>(`${INVOICING}/mutations/chargeInvoice`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId, payment_method: paymentMethodId ?? null,
  });

// Void an OPEN invoice — the operator's undo for a mistaken issueInvoice (wrong amount/creator/
// label), paired with chargeInvoice under the same options dropdown in the UI. Cancels it
// permanently on Stripe (status -> "void"); it can never be charged after this, by chargeInvoice
// OR the customer's own emailed hosted-invoice link. 409 means it isn't open (already paid/void,
// or still an unfinalized draft — see finalizeInvoice). Does NOT un-send the email issueInvoice
// already sent — the customer can still see the invoice, it just stops being payable.
export interface VoidInvoiceResult {
  a_id: number;
  stripe_invoice_id: string;
  status: string;
}

export const voidInvoice = (aId: string, stripeInvoiceId: string) =>
  api.post<VoidInvoiceResult>(`${INVOICING}/mutations/voidInvoice`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId,
  });

// Email an OPEN invoice to the creator again — a payment reminder. Re-sends Stripe's own invoice
// email with its payment link (the same send issueInvoice does at issue time), so the creator
// really gets an email. 409 if it isn't open, or if a reminder already went out in the last 10
// minutes (almost always a double click).
export interface RemindInvoiceResult {
  a_id: number;
  stripe_invoice_id: string;
  reminder_sent_at: string;
}

export const remindInvoice = (aId: string, stripeInvoiceId: string) =>
  api.post<RemindInvoiceResult>(`${INVOICING}/mutations/remindInvoice`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId,
  });

// The actual payment behind a creator's invoice — its Stripe PaymentIntent's own description
// (what Stripe's Dashboard Payments list shows, and what relabelPayment edits), amount, and
// status. `available: false` means no payment could be found (not charged yet, or charged before
// this tracking existed and no amount-matching PaymentIntent turned up either) — every other
// field is then null. See socialiq_invoicing/core/stripe_client.py's get_payment_details.
export interface PaymentDetailsRow {
  available: boolean;
  payment_intent_id: string | null;
  description: string | null;
  amount_cents: number | null;
  currency: string | null;
  status: string | null;
  created_at: string | null;
  /** The invoice's own description (e.g. "Social iQ Boosting <Month> <Year>") — always present
   * regardless of `available`, since it comes from the invoice itself, not from the payment.
   * relabelPayment's default text — guaranteed to match the invoice's own wording exactly. */
  invoice_description: string;
}

export const getPaymentDetails = (aId: string, stripeInvoiceId: string) =>
  api.post<PaymentDetailsRow>(`${INVOICING}/queries/getPaymentDetails`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId,
  });

// Operator action: relabel a payment's description on Stripe (its PaymentIntent + Charge) — for
// one that still reads the generic "Payment for Invoice" default (charged before the automatic
// relabel existed, or its own best-effort relabel failed). 404 means no payment could be found to
// relabel at all (see getPaymentDetails' available flag).
export const relabelPayment = (aId: string, stripeInvoiceId: string, description: string) =>
  api.post<PaymentDetailsRow>(`${INVOICING}/mutations/relabelPayment`, {
    a_id: Number(aId), stripe_invoice_id: stripeInvoiceId, description,
  });

// A creator's saved Stripe US bank accounts, for the "pick a payment method" flow above. Invoices
// are ACH-only, so cards are never listed; `brand`/`exp_month`/`exp_year` are always null.
export interface PaymentMethodRow {
  id: string;
  type: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  bank_name: string | null;
  account_type: string | null;
}

export const listPaymentMethods = (aId: string) =>
  api.post<PaymentMethodRow[]>(`${INVOICING}/queries/listPaymentMethods`, { a_id: Number(aId) });

// Read LIVE from Stripe (matched to a creator by customer email) — not this service's own DB,
// which only ever recorded the status Stripe returned at creation time. See
// socialiq_invoicing/core/mutation.py's listIssuedInvoices docstring. A creator can now appear
// MORE THAN ONCE for the same (year, month) — one row per invoice, any type/label.
export interface IssuedInvoiceRow {
  a_id: number;
  email: string;
  /** Parsed from the invoice's description — "Boosting"/"Impact Bonus" for those two fixed types, or
   * the operator's own text for a custom invoice. */
  label: string;
  /** The invoice's full Stripe description, e.g. "Social iQ Boosting June 2026". */
  description: string;
  amount_cents: number;
  currency: string;
  stripe_invoice_id: string;
  status: string;
  live_mode: boolean;
  created_at: string;
  /** ISO timestamp Stripe will auto-finalize + auto-charge this invoice on, if it's a scheduled
   * draft (status === "draft" with this set — confirmed identical to Stripe's own dashboard
   * "Schedule charge date" behavior, not an error state). Null for non-scheduled invoices. */
  scheduled_charge_at?: string | null;
  /** ISO timestamp of the invoice's "payment due" date (the operator-selected date passed to
   * issueInvoice) — informational only on Stripe's side (see issueInvoice's docstring), but used
   * client-side to tell an "Open" invoice apart from one that's now "Past Due". Null if Stripe
   * has no due_date on the invoice (shouldn't happen for anything issued through this flow). */
  due_date?: string | null;
  /** `null` if this invoice isn't paid (nothing to check yet). For a paid invoice, the SAME exact
   * check the drill-down's getPaymentDetails does — the real PaymentIntent/Charge description,
   * fetched from Stripe, tested for an actual Social iQ/SIQ mention. `false` covers both "never
   * relabeled" (still Stripe's generic "Payment for Invoice" default) and "relabeled to something
   * that still doesn't mention it" — a metadata-presence heuristic alone missed the second case. */
  payment_description_ok?: boolean | null;
  /** Whether a charge attempt already exists for this invoice through this service's own
   * chargeInvoice — a free metadata-presence proxy, not a settlement guarantee. Distinguishes
   * "open, an ACH charge is in flight and just hasn't settled yet" from "open, nobody has
   * attempted to charge it" — Stripe leaves both at status "open" until the charge settles. */
  charge_submitted: boolean;
  // When an operator last re-sent this invoice by email (remindInvoice), ISO-8601 UTC; null if never.
  reminder_sent_at?: string | null;
  /** Past-month adjustment lines carried on this invoice (signed: negative = credit). */
  adjustments?: { settles_month: string; amount_cents: number }[];
  /** Signed sum of this invoice's custom lines (fees and discounts) — part of amount_cents and of
   * what is paid, but not billing for the month. */
  custom_lines_cents?: number;
}

export const listIssuedInvoices = (year: number, month: number) =>
  api.post<IssuedInvoiceRow[]>(`${INVOICING}/queries/listIssuedInvoices`, { year, month });

// ── Creators-admin port (CortexV2 /creators-admin, creator_management role) ───────────────────

// Each advertiser's most recent revenue-share row (advertiser_rev_share has no natural "current"
// row — this is the latest-by-created rollup the Revenue Share tab reads; writes (both new rows
// and edits to this specific row, by ars_id) go via the plain advertiserRevShare CRUD resource).
export interface AdvertiserRevShareRow {
  ars_id: number;
  a_id: number;
  value_mb: number | null;
  value_mmbs: number | null;
  created: string | null;
  advertisername: string | null;
}

export const getAdvertiserRevShareLatest = () =>
  api.post<AdvertiserRevShareRow[]>(`${SQL}/queries/getAdvertiserRevShareLatest`, {});

// The advertiser_rev_share row in effect on `asOf` (YYYY-MM-DD, the last day of a billing month):
// the latest row created on or before it, or the earliest row if none is that early. One row per
// a_id; omit aId for every advertiser. value_mmbs is SocialIQ's split — the Invoices loss test
// divides the SocialIQ bonus by it to find the part of the bonus pool SocialIQ helped reach.
export interface AdvertiserRevShareAsOfRow {
  ars_id: number;
  a_id: number;
  value_mb: number | null;
  value_mmbs: number | null;
  created: string | null;
}

export const getAdvertiserRevShareAsOf = (asOf: string, aId?: string) =>
  api.post<AdvertiserRevShareAsOfRow[]>(`${SQL}/queries/getAdvertiserRevShareAsOf`,
    { as_of: asOf, ...(aId ? { a_id: Number(aId) } : {}) });

// ── Partner Network (CortexV2 /partner-network, partner_network role) ─────────────────────────

// Each publisher's most recent revenue-share row (publishers_rev_share has no natural "current"
// row — this is the latest-by-created rollup the Publisher Rev Share tab reads; writes go via the
// plain publishersRevShare CRUD resource).
export interface PublisherRevShareRow {
  prs_id: number;
  p_id: number;
  value_ad_cost_split: number | null;
  value_publisher_split: number | null;
  created: string | null;
  publishername: string | null;
}

export const getPublisherRevShareLatest = () =>
  api.post<PublisherRevShareRow[]>(`${SQL}/queries/getPublisherRevShareLatest`, {});

// SOC-214_2 — the Amazon tags claimable RIGHT NOW: active, unallocated, past their cooldown. Not
// the generic `amazon_tags` list, which shows every tag and so offers ones the server 409s on.
export type AmazonTagAssignableRow = { at_id: number; tag: string; a_id: number };
export const getAmazonTagsAssignable = () =>
  api.post<AmazonTagAssignableRow[]>(`${SQL}/queries/getAmazonTagsAssignable`, {});

// Each publisher/merchant pair's most recent CPC row (publishers_cpc has no Retool precedent —
// new to the Publisher CPC tab). A publisher can carry more than one CPC, one per merchant, so
// unlike PublisherRevShareRow this is keyed on (p_id, merchant), not p_id alone.
export interface PublisherCpcRow {
  pcpc_id: number;
  p_id: number;
  merchant: string | null;
  cpc: number | null;
  created: string | null;
  publishername: string | null;
}

export const getPublisherCpcLatest = () =>
  api.post<PublisherCpcRow[]>(`${SQL}/queries/getPublisherCpcLatest`, {});

// Daily click history for one redirect (Impact Redirects tab's click-stats drill-in).
export interface RedirectorClicksRow {
  date: string | null;
  clicks: number | null;
}

export const getRedirectorClicksByDay = (r_id: string) =>
  api.post<RedirectorClicksRow[]>(`${SQL}/queries/getRedirectorClicksByDay`, { r_id });

// Per-redirect, per-day clicks/payout/GMV/EPC (Impact, sub_id2='tr52') — Campaign Redirects tab's
// profit report.
export interface RedirectorProfitStatsRow {
  date: string | null;
  r_id: string | null;
  redirectname: string | null;
  publishername: string | null;
  total_clicks: number | null;
  total_payout: number | null;
  total_gmv: number | null;
  EPC: number | null;
}

export const getRedirectorProfitStats = () =>
  api.post<RedirectorProfitStatsRow[]>(`${SQL}/queries/getRedirectorProfitStats`, {});

// Daily click history for one campaign (campaigns_daily_clicks) — Campaign Redirects tab's per-row
// "Clicks Stats" drill-in, which replaced the tab's old Profit Report sub-table. Same per-entity
// scoping as getRedirectorClicksByDay (r_id) / getAmazonInternalTagsClicksByDay (ait_id).
export interface CampaignsDailyClicksRow {
  cdc_id: number | null;
  c_id: string | null;
  clicks: number | null;
  date: string | null;
}

export const getCampaignsDailyClicks = (c_id: string) =>
  api.post<CampaignsDailyClicksRow[]>(`${SQL}/queries/getCampaignsDailyClicks`, { c_id });

// Amazon Internal Tags tab (Partner Network) reports.

export interface AmazonTagMappingRow {
  tags: string | null;  // JSON array (as a string) of real amazon_tags.tag values
}

export const getAmazonTagMappingDetails = (ait_id: number) =>
  api.post<AmazonTagMappingRow[]>(`${SQL}/queries/getAmazonTagMappingDetails`, { ait_id });

export interface AmazonInternalTagClicksRow {
  date: string | null;
  internal_tag: string | null;
  clicks: number | null;
}

export const getAmazonInternalTagsClicksByDay = (ait_id: number) =>
  api.post<AmazonInternalTagClicksRow[]>(`${SQL}/queries/getAmazonInternalTagsClicksByDay`, { ait_id });

export interface AmazonRedirectorProfitStatsRow {
  date: string | null;
  sub_id3: string | null;
  payout: number | null;
  gmv: number | null;
  clicks: number | null;
}

export const getAmazonRedirectorProfitStats = () =>
  api.post<AmazonRedirectorProfitStatsRow[]>(`${SQL}/queries/getAmazonRedirectorProfitStats`, {});

// Every amazon_tags row with its commission revenue in an optional date range — the Amazon Tags
// tab's revenue column (the raw amazon_tags CRUD list has no revenue figures).
export interface AmazonTagsRevenueRow {
  at_id: number;
  tag: string;
  a_id: number | null;
  daily_cap: number | null;
  allocated: number | null;
  allocated_source: string | null;
  active: number;
  latest_last_updated: string;
  revenue: number;
}

export const getAmazonTagsRevenue = (startDate?: string, finishDate?: string) =>
  api.post<AmazonTagsRevenueRow[]>(`${SQL}/queries/getAmazonTagsRevenue`, {
    start_date: startDate ?? null, finish_date: finishDate ?? null,
  });

// Distinct a_ids that have at least one Amazon tag — lets the tab's creator picker default to
// only creators who already have a tag, without pulling the (gated) full table.
export const getAmazonTagsCreatorIds = () =>
  api.post<{ a_id: number }[]>(`${SQL}/queries/getAmazonTagsCreatorIds`, {});

// Validates a creator's Impact account credentials against Impact's own API — pair with
// impactAccounts.create/update (api/resources.ts) to actually save. Does not write anything itself.
export const validateImpactToken = (accountSid: string, token: string) =>
  api.post<{ valid: boolean }>(`${SQL}/impact/validate-token`, { account_sid: accountSid, token });

// Pulls an advertiser's Impact programs into impact_programs (upsert, safe to re-run). Returns the
// number of program rows written; 0 means no active impact_accounts row for this a_id, or Impact
// returned nothing.
export const pullImpactProgram = (aId: string | number) =>
  api.post<{ pulled: number }>(`${SQL}/impact/pull-programs`, { a_id: Number(aId) });

// Every (advertiser, Impact program) pair — the Goals tab's per-creator program picker. Distinct
// from getImpactPrograms (unfiltered, no a_id — the Recipe picker) and getImpactProgramsByAdvertiser
// (filtered to one a_id) — this one is unfiltered AND carries a_id per row.
export interface ImpactProgramWithAdvertiserRow {
  a_id: number;
  campaign_id: number;
  campaign_name: string;
}

export const getImpactProgramsWithAdvertiser = () =>
  api.post<ImpactProgramWithAdvertiserRow[]>(`${SQL}/queries/getImpactProgramsWithAdvertiser`, {});

// Each creator's current-month bonus progress vs. their bonus_targets goals — the Current Status tab
// (read-only; no CRUD table backs it, it's a computed rollup).
export interface BonusCurrentStatusRow {
  a_id: number;
  valor_actual: number | null;
  objetivos_cumplidos: string | null;
  siguiente_objetivo: number | null;
  falta_para_siguiente: number | null;
}

export const getBonusCurrentStatus = () =>
  api.post<BonusCurrentStatusRow[]>(`${SQL}/queries/getBonusCurrentStatus`, {});


/**
 * Ad account → advertiser links, from the adnet sync.
 *
 * TWO TABLES HOLD ACCOUNTS. Creator Management's Facebook Accounts tab lists `fb_accounts` (what
 * requests.fa_id references), but only 2 of its rows carry an `a_id`; the links were actually filled
 * in on `facebook_accounts_adnet` (17 of 21). So the tab resolves an account's advertiser through
 * this, matching on the account id rather than fa_id — one table stores `act_123`, the other `123`.
 */
export interface AdnetAccountLink {
  fa_id: number | null;
  account_id: string;
  a_id: number;
  advertiser_name: string | null;
  has_config: number;
}
export const getAdnetAccountLinks = () =>
  api.post<AdnetAccountLink[]>(`${SQL}/queries/getAdnetAccountLinks`, {});

/**
 * The parents a launch can go under — what the brand's ad account ALREADY runs.
 *
 * Queried live from Meta on each call and never stored, so a campaign renamed, paused or archived in
 * Ads Manager is not still offered here. Call it with no `campaign_id` for the account's campaigns,
 * with one for that campaign's ad sets.
 *
 * Each ad set carries its own `campaign_id`: `ads_into_adset` needs BOTH ids, and an ad-set id alone
 * makes the pipeline create a campaign nobody asked for — so the UI derives the campaign from the
 * chosen ad set instead of asking for it a second time.
 */
export interface MetaParent {
  id: string;
  name?: string;
  status?: string;
  effective_status?: string;
  objective?: string;        // campaigns
  campaign_id?: string;      // ad sets — the parent to send alongside
  campaign_name?: string;    // ad sets, when listed as copy destinations — for grouping
  daily_budget?: string;     // ad sets, minor units
  optimization_goal?: string;
  created_time?: string;
}

/**
 * Live: ad sets a single-ad COPY can be dropped into — every ad set on the source ad's ad account,
 * each carrying its campaign's id and name so the picker can group them.
 *
 * Resolved from the ad id alone, deliberately. The caller used to pass the row's breakdown parent,
 * which at ad level is the AD SET, so Meta was asked for the ad sets under an ad set and answered
 * with none — the picker offered nothing but "a new ad set". The scope is the whole account, not the
 * ad's own campaign: copying an ad into a DIFFERENT campaign is a main reason to copy one at all.
 */
export const getCloneParents = (ad_id: string) =>
  api.get<{ ok: boolean; campaign_id?: string; account_id?: string; adsets?: MetaParent[];
            error?: string }>(
    `${LAUNCHER}/clone/parents?ad_id=${encodeURIComponent(ad_id)}`);

// ── Creator CRM (SOC-604, CortexV2 /creator-crm, creator_crm role) ─────────────────────────────
// Two source lists — pre-approval (advertiser_onboarding) and post-approval (advertisers) — fetched
// unfiltered and grouped/searched client-side (see useCreatorCRM.ts): the whole roster is small
// enough (tens to low hundreds of rows, per the real Monday.com data this replaces) that six
// separate server-filtered calls just for nav-bucket counts would be more round-trips for no
// real benefit. `subitems`/`programs`/`boosting_programs` come back as RAW JSON STRINGS (no
// reflected column type on a raw named-query SELECT to auto-decode through) — parse them, don't
// index into them.
//
// Both lists' full CRM record (scalar fields beyond `programs`/`next_action`) and note history are
// fetched ON DEMAND when the drawer opens — getCreatorCrmDetail/getCreatorNotes below — not
// embedded here. advertisers.crm/advertiser_onboarding.notes_log were JSON columns; SOC-604 moved
// them to real tables (advertisers_crm, creator_notes).

export interface CreatorOnboardingListRow {
  onboarding_id: number;
  a_id: number | null;
  creator_name: string | null;
  primary_handle: string | null;
  boosting_on: string | null;
  assignee: string | null;
  status: "applied" | "onboarding" | "rejected" | "approved" | null;
  notes: string | null;
  timeline_start: string | null;
  timeline_end: string | null;
  priority: string | null;
  status_date: string | null;
  subitems: string | null;   // JSON string: CreatorSubitem[]
  socials: string | null;    // JSON string: OnboardingSocial[] — null for a row added by hand or before the column
  approved: string | null;
  created: string | null;
  updated: string | null;
  // Links this pre-approval row back to the JoyLink account that applied — set at creation for a
  // waitlist applicant moved into the pipeline (see useMarkWaitlistOnboarding in useCreatorCRM.ts),
  // null for a manually-added row. The Applied tab dedupes joylink boosting_waitlist rows against
  // this (and against advertisers.joylink_id) so an applicant already in the pipeline isn't shown twice.
  joylink_id: string | null;
  // Applicant contact details, carried over from a joylink boosting_waitlist row on "Mark as
  // Onboarding" (see useMarkWaitlistOnboarding) — null for a manually-added row.
  email: string | null;
  phone: string | null;
  monthly_gmv: string | null;   // free text as the applicant entered it — display as-is, don't parse
  entity_name: string | null;
}

export const getCreatorOnboardingList = (params: { status?: string; assignee?: string; search?: string } = {}) =>
  api.post<CreatorOnboardingListRow[]>(`${SQL}/queries/getCreatorOnboardingList`, {
    status: params.status ?? null, assignee: params.assignee ?? null, search: params.search ?? null,
  });

export interface CreatorCrmListRow {
  a_id: number;
  advertiserName: string | null;
  realname: string | null;     // advertisers.realname — the roster table's Real Name column
  email: string | null;        // advertisers.email — the roster table's Email column
  active: number | null;
  joylink_id: string | null;
  phone: string | null;        // advertisers.phone — carried here for the roster table column
  programs: string | null;     // JSON string: string[] (advertisers_crm.boosting_programs)
  next_action: string | null;  // advertisers_crm.next_action — carried here for the roster table column
  owner: string | null;        // advertisers_crm.owner — backs the Owner filter (default: current user)
  handles: string | null;  // JSON string: { platform: string; handle: string }[]
  last_upload: string | null;
  impact_connected: number | null;
}

export const getCreatorCrmList = (params: { active?: 0 | 1; program?: string; search?: string } = {}) =>
  api.post<CreatorCrmListRow[]>(`${SQL}/queries/getCreatorCrmList`, {
    active: params.active ?? null, program: params.program ?? null, search: params.search ?? null,
  });

export interface CreatorGmvSummaryRow {
  a_id: number;
  impact_gmv: number | null;
  amazon_gmv: number | null;
  total_gmv: number | null;
}

// a_id_in is sent as STRINGS, not numbers — see socialiq_bqv/queries/optimization.py's
// CreatorGmvSummaryParams for why (an empty array's BigQuery param type can't be inferred from its
// elements, so it must default to the same type as a non-empty string array; comparing that against
// an INT64 column is a compile-time BigQuery error otherwise — confirmed against real BigQuery).
export const getCreatorGmvSummary = (params: { aIdIn?: number[]; startDate?: string; endDate?: string } = {}) =>
  api.post<CreatorGmvSummaryRow[]>(`${BQV}/queries/getCreatorGmvSummary`, {
    a_id_in: (params.aIdIn ?? []).map(String), start_date: params.startDate ?? null, end_date: params.endDate ?? null,
  });

export interface CreatorTasksFlatRow {
  onboarding_id: number;
  creator_name: string | null;
  title: string | null;
  status: "open" | "in_progress" | "done" | "not_necessary" | null;
  assignee: string | null;
  due_date: string | null;
}

export const getCreatorTasksFlat = (assignee?: string) =>
  api.post<CreatorTasksFlatRow[]>(`${SQL}/queries/getCreatorTasksFlat`, { assignee: assignee ?? null });

// Parsed shape for the subitems JSON-string field above — mirrors
// socialiq_sql/resources/advertiser_onboarding.py's Subitem.
export interface CreatorSubitem {
  title: string;
  status: "open" | "in_progress" | "done" | "not_necessary";
  assignee?: string | null;
  due_date?: string | null;
}

// One row of getCreatorCrmDetail — mirrors socialiq_sql/resources/advertisers_crm.py's
// AdvertiserCrm. Fetched on demand (array of 0 or 1) when the drawer opens for an approved
// creator; `programs`/`next_action` are already on the CreatorCrmListRow the drawer was opened
// from, so this covers everything else.
export interface CreatorCrmDetailRow {
  advertiser_crm_id: number | null;
  a_id: number | null;
  business_entity: string | null;
  client_focus: string | null;
  content_permission: "No" | "Approval required" | "Never asked" | "Need permission" | "Yes" | null;
  payment_ach: number | null;
  payment_cc: number | null;
  communication_channel: string | null;
  second_email: string | null;
  phone: string | null;
  link: string | null;
  long_text: string | null;
  other_platforms: string | null;
  product_types: string | null;
  additional_information: string | null;
  owner: string | null;
  official_start_date: string | null;
  paused_at: string | null;
  renewal_date: string | null;
  next_action: string | null;
  created: string | null;
  updated: string | null;
}

export const getCreatorCrmDetail = (aId: number) =>
  api.post<CreatorCrmDetailRow[]>(`${SQL}/queries/getCreatorCrmDetail`, { a_id: aId });

// One row of getCreatorNotes — the creator_notes table, for either lifecycle stage. Replaces the
// old embedded notes_log JSON array; fetched on demand, same as getCreatorCrmDetail.
export interface CreatorNoteRow {
  note_id: number;
  a_id: number | null;
  onboarding_id: number | null;
  author: string;
  body: string;
  created: string | null;
}

export const getCreatorNotes = (target: { aId: number } | { onboardingId: number }) =>
  api.post<CreatorNoteRow[]>(`${SQL}/queries/getCreatorNotes`, {
    a_id: "aId" in target ? target.aId : null,
    onboarding_id: "onboardingId" in target ? target.onboardingId : null,
  });

// Default onboarding checklist, seeded the first time a creator's row moves into "onboarding" —
// mirrors socialiq_sql/core/mutation.py's DEFAULT_ONBOARDING_SUBITEM_TITLES (keep the two lists in
// sync). This copy is what useMarkWaitlistOnboarding sends on CREATE in useCreatorCRM.ts: a
// brand-new row can never already have tasks to preserve, so it always sends the defaults rather
// than round-tripping through markCreatorOnboarding below (whose backend copy is the one that has
// to check first, since an EXISTING row might already have a checklist from a prior onboarding
// stint before a rejection).
export const DEFAULT_ONBOARDING_SUBITEM_TITLES = [
  "Onboarding Call", "Meta Ads Partnership", "Retool Update", "Amazon Tags",
  "Content Upload", "SB Testing",
] as const;

export const defaultOnboardingSubitems = (): CreatorSubitem[] =>
  DEFAULT_ONBOARDING_SUBITEM_TITLES.map(title => ({ title, status: "open" }));

export const markCreatorOnboarding = (onboardingId: number) =>
  api.post<{ message: string; onboarding_id: number; seeded_default_tasks: boolean }>(
    `${SQL}/mutations/markCreatorOnboarding`, { onboarding_id: onboardingId });

// One entry of advertiser_onboarding.socials — mirrors socialiq_sql/resources/advertiser_onboarding.py's
// Social. approveCreatorOnboarding copies these into advertisers_socials on its own.
export interface OnboardingSocial { handle: string; platform: string | null }

export const approveCreatorOnboarding = (onboardingId: number) =>
  api.post<{ message: string; a_id: number; onboarding_id: number; linked_existing_advertiser: boolean }>(
    `${SQL}/mutations/approveCreatorOnboarding`, { onboarding_id: onboardingId });

// No author param — the backend derives it from the authenticated caller (never client-supplied),
// same as saveTimeFilterPreset's owner_email; see core/mutation.py's AppendCreatorNoteBody.
export const appendCreatorNote = (target: { aId: number } | { onboardingId: number }, body: string) =>
  api.post<{ message: string }>(`${SQL}/mutations/appendCreatorNote`, {
    a_id: "aId" in target ? target.aId : null,
    onboarding_id: "onboardingId" in target ? target.onboardingId : null,
    body,
  });

// Author-only on the backend (403 for anyone else) — the UI also hides the buttons on others' notes.
export const updateCreatorNote = (noteId: number, body: string) =>
  api.post<{ message: string }>(`${SQL}/mutations/updateCreatorNote`, { note_id: noteId, body });

export const deleteCreatorNote = (noteId: number) =>
  api.post<{ message: string }>(`${SQL}/mutations/deleteCreatorNote`, { note_id: noteId });

// ── JoyLink boosting waitlist (SOC-604, socialiq-supabase) ─────────────────────────────────────
// The Applied tab's other source: joylink's own waitlist form submissions, read through
// socialiq-supabase's read-only proxy onto joylink's Supabase project (see that service's README —
// GET-only, one table allowlisted). READ-ONLY ON PURPOSE: the actual accept/reject decision (and the
// Loops acceptance email + users.boostingWaitlistAcceptedAt update) happens in joylink-platform's own
// admin page — writing an acceptance/rejection here would silently bypass both. What this UI CAN do
// is move an applicant into the advertiser_onboarding pipeline — at "onboarding"
// (useMarkWaitlistOnboarding, which optionally fires joylink's accept call below) or at "rejected"
// (useRejectWaitlistApplicant, Cortex-only: joylink has no reject endpoint) — a Cortex-side record,
// never a write to the waitlist application itself.
// JoyLink's own onboarding-status bucket (getOnboardingStatus / hasStartedSocialIqOnboarding in
// WaitlistDashboard.tsx + route.ts) — DISPLAYED on the Applied tab, not used for bucketing which
// rows appear there (see the `waitlistRows` comment in CreatorCRMView.tsx: that's still "not yet
// linked to a Cortex onboarding row/advertiser", pending a product decision on whether to fold
// JoyLink's status into it).
export type JoylinkWaitlistStatus =
  | "Applied" | "Incomplete" | "Accepted" | "In progress" | "Onboarded" | "Payment failed";

export interface BoostingWaitlistRow {
  id: string;
  user_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  monthly_gmv: string;   // free text as entered ("150,000", "$1000", "3 million") — display as-is, don't parse
  social_handles: { value: string }[];
  affiliate_programs: string[];
  selected_affiliate_networks: string[] | null;
  entity_name: string | null;
  state: string | null;
  affiliate_program_other: string | null;
  submitted_at: string;
  updated_at: string | null;
  created_at: string;
  sms_consent_at: string | null;
  // JoyLink Managed charge (joylink-platform JOY-1168): accepting on JoyLink charges the first
  // $1,000/mo to the payment method the applicant saved with their application. JoyLink refuses to
  // accept until they've agreed to the charge and added one (see canChargeOnAcceptance).
  charge_consent_at: string | null;
  payment_method_added: boolean;
  managed_subscription_id: string | null;
  payment_failed_at: string | null;
  payment_failure_reason: string | null;
  // Every trialing/active/past_due subscription they already have, newest first — Managed is charged
  // on top of these.
  active_subscriptions: { product_name: string; status: string; is_managed: boolean }[];
  // Joined in server-side (socialiq_supabase's fixed boosting_waitlist query, SOC-604) to
  // reproduce joylink-platform's own admin waitlist dashboard columns — see that service's
  // core/resource.py for the query.
  accepted_at: string | null;
  social_iq_boosting_onboarding_completion_email_sent_at: string | null;
  social_iq_boosting_agreement_signed_at: string | null;
  social_iq_boosting_billing_terms_accepted_at: string | null;
  is_enterprise: boolean | null;
  plan: string;   // "Enterprise" | "Pro" | "Free" | "Guest" | another Stripe product name verbatim
  link_count: number;
  current_month_clicks: number;
  total_clicks: number;
  // True only for a row WITH a linked joylink user_id but no metrics snapshot row for them yet —
  // render Links/Clicks as "—", not 0, when this is set (matches joylink's own metricsMissing).
  metrics_missing: boolean;
  joylink_status: JoylinkWaitlistStatus;
}

export function canChargeOnAcceptance(row: BoostingWaitlistRow): boolean {
  return row.payment_method_added && row.charge_consent_at != null;
}

// Stripe doesn't email creators about failed or missing payment methods, so operators reach out.
// Same copy as joylink's own admin waitlist sheet.
export const PAYMENT_FAILED_HINT =
  "Stripe doesn't email the creator about this. Contact them and ask them to add a different payment method at joylink.io/boosting.";
export const MISSING_PAYMENT_METHOD_HINT =
  "Contact the creator and ask them to finish their application at joylink.io/boosting: agree to the charge and add a payment method.";

export const getBoostingWaitlist = () =>
  api.get<BoostingWaitlistRow[]>(`${SUPABASE}/boosting_waitlist?limit=1000`);

// Calls joylink-platform's OWN admin waitlist API (via socialiq-supabase's proxy, core/joylink_admin.py)
// so "Mark as Onboarding" triggers the same side effects joylink's own admin dashboard would: the
// JoyLink Managed charge, the Loops acceptance email and the users.boostingWaitlistAcceptedAt update.
// A failed charge is a 402 whose message is the reason ("The creator's card was declined (…)"), and
// the applicant is NOT accepted. PROD-ONLY on the backend — it 403s outside
// METRICS_ENVIRONMENT=="prod" (no staging joylink endpoint exists). Callers (useMarkCreatorOnboarding
// / useMarkWaitlistOnboarding in useCreatorCRM.ts) run it BEFORE the Cortex-side change and stop if it
// fails, so Cortex never shows someone as onboarding who JoyLink didn't accept and charge.
export const markJoylinkWaitlistOnboarding = (joylinkUserId: string) =>
  api.post<{ success?: boolean }>(`${SUPABASE}/boosting_waitlist/mark_onboarding`, {
    user_id: joylinkUserId, action: "accept",
  });

export function isUrlLike(v: string): boolean {
  return /^https?:\/\//i.test(v.trim());
}

// Reduces a raw social_handles[].value to a plain handle (no leading '@') for
// advertiser_onboarding.primary_handle, which the CRM roster renders as `@${primary_handle}` — a
// full URL stored there would render as a mangled `@https://...`. A URL's last non-empty path
// segment stands in for the handle (instagram.com/shopswithsarah -> shopswithsarah); anything else
// is used as-is with any leading '@' stripped.
export function waitlistPrimaryHandle(value: string): string {
  const v = value.trim();
  if (isUrlLike(v)) {
    try {
      const segments = new URL(v).pathname.split("/").filter(Boolean);
      return segments[segments.length - 1] ?? v;
    } catch { return v; }
  }
  return v.replace(/^@/, "");
}

// Display form of a waitlist social handle (roster table + search) — '@handle' for a bare handle,
// or the raw URL unchanged (the drawer renders that case as a link instead of prefixing it).
export function waitlistDisplayHandle(value: string): string {
  const v = value.trim();
  return isUrlLike(v) ? v : (v.startsWith("@") ? v : `@${v}`);
}

// A waitlist social_handles[].value carries no platform field of its own (unlike an approved
// creator's `platforms` object, keyed by platform name) — it's either a bare handle or a raw
// profile URL. Sniffed from the URL's host so the Applied drawer can show the same platform icon
// (CreatorsAdminView.tsx's PlatformIcon/PLATFORM_LOGOS) an approved creator's Platforms column
// does. Returns a PLATFORM_LOGOS key, or null (bare handle, or an unrecognized domain) — the
// caller's PlatformIcon falls back to a generic glyph either way.
const SOCIAL_HANDLE_HOST_PLATFORMS: [pattern: RegExp, platform: string][] = [
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)tiktok\.com$/, "tiktok"],
  [/(^|\.)(facebook|fb)\.com$/, "facebook"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "youtube"],
  [/(^|\.)pinterest\.com$/, "pinterest"],
  [/(^|\.)(twitter\.com|x\.com)$/, "x"],
  [/(^|\.)threads\.net$/, "threads"],
];

// A joylink application's social_handles as advertisers_socials entries, in the applicant's order:
// the handle (last URL path segment, or the bare handle, '@' stripped) plus the platform sniffed from
// the URL's host (null for a bare handle or an unknown domain). Values that leave no handle, or one
// longer than the column's varchar(100), are dropped rather than failing the whole approval.
export function socialsFromWaitlist(row: BoostingWaitlistRow): OnboardingSocial[] {
  return (row.social_handles ?? []).flatMap(({ value }) => {
    const handle = waitlistPrimaryHandle(value ?? "").replace(/^@/, "");
    if (!handle || handle.length > 100) return [];
    return [{ handle, platform: platformFromSocialHandleValue(value) }];
  });
}

export function platformFromSocialHandleValue(value: string): string | null {
  const v = value.trim();
  if (!isUrlLike(v)) return null;
  try {
    const host = new URL(v).hostname.replace(/^www\./, "");
    const hit = SOCIAL_HANDLE_HOST_PLATFORMS.find(([pattern]) => pattern.test(host));
    return hit ? hit[1] : null;
  } catch {
    return null;
  }
}

// ── SOC-761 social scraper (socialiq-content) ───────────────────────────────────────────────────
// Creators' own Instagram/Facebook/TikTok posts, crawled through Smart Crawl and reviewed in the
// Creator CRM. Queueing a crawl spends money (every URL is a billed vendor call), which is why the
// backend enforces a per-creator cooldown and refuses a second run while one is in flight — this
// client never decides that for itself.

export interface ScrapedMedia {
  advertisers_scraped_media_id: number;
  position: number;
  media_type: "image" | "video";
  url: string | null;              // our GCS copy; null until the download succeeds
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
  duration_seconds: number | null;
  download_status: "pending" | "stored" | "failed" | "unavailable";
  download_error: string | null;
  promoted_ac_id: number | null;   // set once this slide is published to the content library
}

/** What the reviewer chose, read back off the advertisers_content rows this post was published
 *  as. `merchant_urls` here — not `product_links` — is what decides whether the autolauncher can
 *  launch the asset, and the two diverge the moment anyone sets a real merchant. Null until the
 *  post is published. */
export interface PublishedAsset {
  ac_ids: number[];
  niche: string | null;
  status: ContentStatus;
  merchant_urls: MerchantUrl[];
  audio_tag: AudioTag | null;
  seasonal: boolean;
  start_date: string | null;
  end_date: string | null;
  requires_starting_hour: boolean;
  starting_hour: number;
}

/** The asset fields a reviewer fills in before publishing. Every field is optional and omitting
 *  one means "leave it alone", so an approval with no edits behaves as it did before SOC-764.
 *  `merchant` on a link must be a real merchant NAME, never a domain — the autolauncher matches on
 *  it, so `liketk.it` publishes an asset that can never launch. */
export interface ScrapedReviewEdits {
  niche?: string;
  status?: ContentStatus;
  merchant_urls?: MerchantUrl[];
  /** Present-and-false CLEARS the window; absent leaves whatever is stored alone. */
  seasonal?: boolean;
  start_date?: string;
  end_date?: string;
  /** Explicit null clears it, for the same reason. */
  audio_tag?: AudioTag | null;
  requires_starting_hour?: boolean;
  starting_hour?: number;
}

export interface ScrapedPost {
  advertisers_scraped_posts_id: number;
  a_id: number;
  platform: string;
  post_id: string;
  permalink: string | null;
  post_type: string | null;
  posted_at: string | null;
  caption: string | null;
  is_paid_partnership: boolean;
  likes: number | null;
  comments: number | null;
  views: number | null;
  /** What the scraper saw, plus where the resolver followed it to.
   *
   * `domain` is the link's OWN hostname, which on real posts is `liketk.it` — a link aggregator,
   * not a retailer. `resolved_host` is where the redirects actually end, and `suggested_merchant`
   * is the merchant name that host implies, derived by the backend on read and stored nowhere:
   * which retailer a link means stays the reviewer's answer. It is null for an aggregator, for a
   * collection page, and for anything we have not resolved yet.
   */
  product_links: {
    url: string; source?: string; domain?: string;
    resolved_url?: string | null; resolved_host?: string | null;
    resolve_status?: "resolved" | "unchanged" | "failed" | "refused" | null;
    resolve_error?: string | null; resolved_at?: string | null;
    suggested_merchant?: string | null;
  }[];
  review_status: "new" | "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  provider: string | null;
  media: ScrapedMedia[];
  published: PublishedAsset | null;
}

export interface SocialCrawlSource {
  advertisers_scraped_source_id: number;
  platform: string;
  handle: string;
  profile_url: string | null;
  enabled: boolean;
  last_crawled_at: string | null;
  error_code: string | null;
  error: string | null;
}

/** A handle on file that never became a source, and why. Derived per request from
 *  `advertisers_socials` and stored nowhere, so it corrects itself the moment the handle does.
 *  Only handles on a network we actually crawl appear here — a linktree row is not a fault. */
export interface SocialCrawlSkipped {
  platform: string;
  handle: string;
  reason: string;
}

export interface SocialCrawlStatus {
  a_id: number;
  crawl_status: "not_requested" | "queued" | "running" | "done" | "failed";
  last_crawled_at: string | null;
  error_code: string | null;
  running: boolean;
  reason?: string | null;
  latest_run: {
    scraped_crawl_run_id: number; status: string; kind: string;
    pages_fetched: number; posts_new: number; posts_updated: number; media_stored: number;
    error_code: string | null; started_at: string | null; finished_at: string | null;
  } | null;
  sources: SocialCrawlSource[];
  skipped?: SocialCrawlSkipped[];
  /** Posts still waiting for a human — what the Content page's tab badge shows. Same predicate as
   *  the "To review" pill inside the tab, so the two can never disagree. */
  to_review?: number;
}

/** Queue a crawl. 202 when this call claimed the creator. A 409 — a run already in flight, or the
 *  creator inside the cooldown — REJECTS with an ApiError whose message is a sentence meant to be
 *  shown as-is ("already crawled 2 hours ago; the next crawl is available in about 10 hours, or use
 *  force to override"). Pass `force` to override the cooldown; every page is a billed vendor call,
 *  which is why it is not the default. */
export const queueSocialCrawl = (aId: number, opts?: { force?: boolean }) =>
  api.post<SocialCrawlStatus>(
    `${CONTENT}/creators/${aId}/social-crawl${opts?.force ? "?force=true" : ""}`, {});

export const getSocialCrawlStatus = (aId: number) =>
  api.get<SocialCrawlStatus>(`${CONTENT}/creators/${aId}/social-crawl`);

export const getScrapedPosts = (aId: number, params?: { limit?: number; offset?: number }) => {
  const q = new URLSearchParams();
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  const suffix = q.toString() ? `?${q}` : "";
  return api.get<{ items: ScrapedPost[]; limit: number; offset: number }>(
    `${CONTENT}/creators/${aId}/scraped-posts${suffix}`);
};

/** Approve or reject one scraped post. Approving publishes the chosen slides into the content
 *  library (one advertisers_content row each) so the autolauncher can run them. */
export const reviewScrapedPost = (
  aspId: number,
  body: {
    review_status: "new" | "approved" | "rejected";
    media_positions?: number[];
    /** Sending this again for an already-published post updates its rows in place. The server owns
     *  that merge, deliberately: it keeps metadata.name and metadata.scraped, which a client-side
     *  partial update through contentItemToRow would erase. */
    edits?: ScrapedReviewEdits;
  },
) => api.post<{ post: ScrapedPost; promoted_ac_ids: number[] }>(
  `${CONTENT}/scraped-posts/${aspId}/review`, body);
